/**
 * AtlasWaterModule — the Commons sea (terrain + water realism wave).
 *
 * Continental basins below `commonsSeaLevel(seed)` fill with the engine's
 * realistic water: a FIXED-footprint plane (the engine's shoreline features
 * need one) that FOLLOWS the player in half-footprint steps. Each recenter
 * re-bakes the engine ShorelineField from `stampedHeight` — same texture
 * object, new texels, no shader rebuild — so waves SHOAL onto real beaches,
 * surf foam hugs the coast, and shallow water tints sand. The wet-sand band
 * on the terrain side lives in the ground shader (wetness.applyBiomeGround).
 *
 * Commons-only: personal worlds get the new terrain, not water. Rendering
 * only — the server does not gate movement/placement against water yet.
 * Sun direction tracks the sky (uSunDir); an absorption-tinted FogExp2
 * swaps in while the camera is submerged (AtmosphereModule's fog object is
 * saved and restored by reference).
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import {
  createRealisticWaterMaterial,
  DEFAULT_REALISTIC_WATER,
  type RealisticWaterHandles,
} from 'enginex/environment/water/RealisticWaterMaterial.js';
import { createWaveSet, filterVertexWaves, sampleHeight, sampleNormal, type GerstnerWave, type WaveModulation } from 'enginex/environment/water/GerstnerWaves.js';
import { cos, sin, uniform } from 'three/tsl';
import { GpuParticleEmitter } from 'enginex/particles/gpu/GpuParticleEmitter.js';
import { CpuParticleAdapter } from 'enginex/particles/gpu/CpuParticleAdapter.js';
import { applySoftParticles } from 'enginex/vfx/SoftParticleModifier.js';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { ShorelineField } from 'enginex/environment/water/ShorelineField.js';
import { UnderwaterOverlay } from 'enginex/environment/water/UnderwaterOverlay.js';
import { generateWaterNormalMap } from 'enginex/environment/WaterSystem.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { ensureWaterField, waterFx, splashWater, stampWater, dripWater, type WaterSurfaceProvider } from '../lib/water_fx.js';
import {
  waterFxTier, craftWaterMode, downwashFor, newDownwash, sprayThisTick, dripRate, hoverSprayPerS, wakeRateScale, RateGate,
  CRAFT_HULL_M, CRAFT_WATER_RANGE_M, CRAFT_WATER_MAX, DOWNWASH_RING_EVERY_S, type WaterFxTier,
} from '../lib/water_interact.js';
import { wakeStampPair } from '../lib/water_physics.js';
import { forEachCraft, reportCraft, type CraftReport } from '../lib/audio/craft_feed.js';
import {
  seaStateFor, windHeadingRotation, slew, slewAngle, AMP_SLEW_PER_S, HEADING_SLEW_RAD_PER_S,
  rainDropsThisTick, RAIN_DROP_STAMP, type SeaState,
} from '../lib/water_physics.js';
import type { WeatherModule } from './WeatherModule.js';
import { FoamAccumulator } from 'enginex/environment/water/FoamAccumulator.js';
import { FoamField } from 'enginex/environment/water/FoamField.js';
import { CausticsProjector } from 'enginex/environment/water/CausticsProjector.js';
import { stage3Stats } from '../lib/stage3_fx.js';
import { createUnderwaterShafts } from 'enginex/environment/water/UnderwaterShafts.js';
import { stage4Stats } from '../lib/stage4_fx.js';
import { sfx } from '../lib/sfx.js';
import { atlas } from '../state.js';
import { postRiverTable } from '../lib/hydrology_load.js';
import { bus } from '../bus.js';
import { meshHeight } from '../lib/terrain_stamp.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { riverWaterAt } from '../../../shared/terrain.js';
import { setUnderwaterFog } from './AtmosphereModule.js';
import { createLakeBake, insertLakeRegion, lakeSurfaceAtBaked, nearestLakeLevel, LAKE_GRID_N, LAKE_REGION_SIZE, type LakeBake, type LakeRegion } from '../lib/lakes.js';
import { buildLakeSheetGeometry, drawnLakeHeight, RIVER_RIBBON_LIFT } from '../lib/drawn_water.js';
import { buildSeaIndex } from '../lib/sea_cull.js';
import type { WaterBakeJob, WaterBakeResult } from '../workers/water_bake_worker.js';
import { uSunDir } from './SkyModule.js';
import { waterSkyTint, waterLight, makeWaterLight } from '../lib/water_lighting.js';
import { lightFrame, lum709 } from '../lib/photometry.js';
import { tightenReadback } from '../lib/readback.js';
// ── Water realism wave (2026-09-25): the look per tier, the shared maps, the far sea, the budget, the medium ──
import {
  waterLookFor, waterLookSignature, waterNormalMaps, waterLookConfig, farSeaHandles, currentFarSea, pickPlanarBody,
  underwaterInscatter, underwaterInscatterLit, NEAR_SEA_FADE, NEAR_SEA_HALF_M, FAR_SEA_CARVE_MARGIN_M, type WaterLook, type PlanarCandidate,
  OPTICS_BY_BODY, underwaterInscatterBody, turbidSigma,
} from '../lib/water_look.js';
import { UnderwaterMedium } from 'enginex/environment/water/UnderwaterMedium.js';
import { setBedWater, bedShadingStats, setBedBodies, setBedBodiesFrame, setSeaBedFix, setBedBodiesDebug } from '../lib/water_bed.js';
// ── Shader gaps R1 (2026-09-29): the water-body descriptor, the lake / river bed table, the near-plane waterline ──
import {
  bodyAt, labelLakeRegion, packBodyTable, riverSegmentsNear, setWaterBodyLabels, setWaterBodySources, waterBodiesEnabled, seaBedFixEnabled,
  waterlineFor, RIVER_RADIUS_M, type BodyTable, type LakeLabels, type WaterBody,
} from '../lib/water_body.js';
import type { NearPlaneWaterline } from 'enginex/environment/water/waterLook.js';
import { UnderwaterLife } from '../lib/underwater_life.js';
import { SplashCrowns } from '../lib/splash_crowns.js';
import { waterPlanarBodies } from '../lib/water_fx.js';
// ── Shader gaps R5 (2026-09-30): the sea's waves behind ONE provider — the Gerstner set, or the FFT patch on ultra/WebGPU ──
import { GerstnerWaveProvider, type WaveProvider } from 'enginex/environment/water/WaveProvider.js';
import { FftWaveProvider } from 'enginex/environment/water/fft/FftWaveProvider.js';
import { FFT_OCEAN, fftOceanDecision, fftOceanKilled, fftSpectrumFor, type FftOceanReason } from '../lib/fft_ocean.js';

/** Water plane footprint (m). Beyond it the sea fades below the horizon fog.
 *  Doubled (unified-render wave): the sea reaches ~1.5 km half-extent to better
 *  match the extended terrain/city draw distance. SEGMENTS + SHORE_RES scale with
 *  it so the 8 m vertex spacing + shoreline texel density (the anti-checkerboard
 *  Nyquist contract) are preserved exactly. */
/** The water clock's wrap (s): the shaders' time stays small for float32, and the sea must be the SAME sea either side. */
export const WATER_CLOCK_WRAP_S = Math.PI * 2 * 1000;
/**
 * FIX (shader sweep C, 2026-10-06): THE LOOPING SEA. The clock wraps at WATER_CLOCK_WRAP_S (float32 time), but a
 * Gerstner phase is w·(d·x) + φ·t with φ = √(g·k): at the wrap every wave jumped by φ·P mod 2π — the whole sea (and
 * every lake) popped once every 1.75 h. Quantize each φ to a multiple of 2π/P (0.001 rad/s, a < 0.05 % speed change)
 * so φ·P is a whole number of turns and the wrap is seamless — Tessendorf's repeat-time trick, which the FFT patch
 * already takes (`loopPeriodS`). In place, before any material or provider reads the set (they share the objects).
 */
export function loopWaveSet(waves: GerstnerWave[], periodS = WATER_CLOCK_WRAP_S): GerstnerWave[] {
  const quantum = (Math.PI * 2) / periodS;
  for (const wv of waves) wv.phi = Math.max(quantum, Math.round(wv.phi / quantum) * quantum);
  return waves;
}

const FOOTPRINT = 3072;
/**
 * Displacement grid segments. 192 -> 8 m vertex spacing over the footprint.
 * WHY: the old 96 (16 m spacing) put EVERY wave of the 22 m-cascade sea
 * below the grid's 32 m Nyquist limit — each vertex sampled the waves at a
 * pseudo-random phase, every quad tilted independently, and the sea read as
 * a checkerboard of flat tiles (user screenshot 2026-08-23). 8 m spacing
 * samples the 22 m swell cleanly; everything shorter is excluded from the
 * vertex stage via vertexWavelengthCutoff and lives in the per-pixel
 * analytic normal instead (which cannot alias on the mesh). Net vertex cost
 * is ~flat: 4x the vertices, but 1 displacement wave in the shader not 6.
 */
const SEGMENTS = 384;
/**
 * Shortest wavelength the sea GEOMETRY displaces (2.5x the 8 m vertex
 * spacing). Waves below it render through the fragment normal/foam only.
 */
const VERTEX_WAVE_CUTOFF = 2.5 * (FOOTPRINT / SEGMENTS);
/** Recenter when the camera strays this far from the bake center. */
const REBAKE_DIST = FOOTPRINT / 4;
/** Recenter snap step — keeps rebakes deterministic per grid cell. */
const SNAP = 64;
/** ShorelineField texels per side (8 m/texel over the footprint). */
const SHORE_RES = 320;
/** surfaces (2026-09-27): the surf band's width from the waterline, metres (engine `shoreline.foamBandM`). */
export const SURF_BAND_M = 4;

export class AtlasWaterModule implements IModule {
  readonly name = 'AtlasWater';

  private app: App | null = null;
  private scene!: THREE.Scene;
  private mesh: THREE.Mesh | null = null;
  /** Boot loading screen: the Commons sea surface is built. */
  get ready(): boolean { return this.mesh !== null; }
  private handles: RealisticWaterHandles | null = null;
  /** Stage 3 (2026-09-09): persistent whitecap trails (CPU foam grid the sea samples) + projected caustics (a small ortho render per frame). Built with the material — the menu rows apply at the next world entry. */
  private foam: FoamAccumulator | null = null;
  private caustics: CausticsProjector | null = null;
  /** R10: the camera-centred GPU foam memory (null below high / without the foam trails). */
  private foamField: FoamField | null = null;
  private readonly _foamDrift = new THREE.Vector2(0.4, 0.3);
  private causticFrame = 0;
  /** Stage 4: light shafts around the submerged camera (built with the overlay, visible only under). */
  private shafts: { mesh: THREE.Mesh; uniforms: { intensity: { value: number }; depth: { value: number }; slant: { value: THREE.Vector2 } } } | null = null;
  /** The last inland/wet probe (per snapped centre) — requestRecenter re-enters every tick while a bake is pending. */
  private lastProbe: { x: number; z: number; wet: boolean } | null = null;
  /** Scratch colors for the per-frame sky-side tint ramp (no allocations). */
  private readonly _skyTint = new THREE.Color();
  /** The water's light this frame — the ground's own photometric numbers (lib/water_lighting, session 3). */
  private readonly _light = makeWaterLight();
  private shoreline: ShorelineField | null = null;
  private normalMap: THREE.DataTexture | null = null;
  private seed = '';
  private seaLevel = 0;
  private centerX = 0;
  private centerZ = 0;
  private time = 0;
  private readonly camPos = new THREE.Vector3();
  // ── Sliced shore bake: bed heights fill a scratch grid a few rows per
  // frame using meshHeight — the RENDERED truth (far chunks interpolate at
  // ~10.7 m spacing, so the analytic field drifts meters off the visible
  // ground on steep shores and the water read as floating). One fast engine
  // rebake then reads the grid. Keeps travel-time recenters hitch-free.
  private readonly scratch = new Float32Array(SHORE_RES * SHORE_RES);
  /** Sea triangles drawn after the last dry cull, of the grid's full count (`__water().seaCull`). */
  private seaCullKept = -1;
  private bakeTarget: { x: number; z: number } | null = null;
  private bakeRow = 0;
  // ── Hydrology lakes: a second, region-following water layer. One mesh +
  // one material live for the session; per region only the geometry and the
  // depth texture's texels change (no shader rebuilds).
  private lakeMesh: THREE.Mesh | null = null;
  private lakeHandles: RealisticWaterHandles | null = null;
  /** The region the lake mesh currently shows (its mirror plane follows the nearest lake). */
  private lakeRegion: LakeRegion | null = null;
  private mirrorTick = 0;
  private readonly _mirrorN = new THREE.Vector3();
  private readonly _mirrorP = new THREE.Vector3();
  private lakeDepthTex: THREE.DataTexture | null = null;
  private readonly lakeBytes = new Uint8Array(LAKE_GRID_N * LAKE_GRID_N);
  private lakeBake: LakeBake | null = null;
  private lakeBakeRow = 0;
  private lakeCenterX = Infinity; // Infinity forces the initial bake
  private lakeCenterZ = Infinity;
  // ── Perf wave 2: bakes run in a Web Worker (terrain_chunk_worker pattern).
  // The sliced main-thread paths remain as the no-Worker fallback.
  private bakeWorker: Worker | null = null;
  private workerBroken = false;
  private jobId = 0;
  private pendingShoreId = 0;
  private pendingLakeId = 0;
  private bakeBeatMs = 0; // last dispatch OR reply — the stall watchdog's heartbeat
  /** true after the worker's FIRST reply — before that it may still be importing its module graph (cold dev server: tens of seconds) */
  private bakeEverReplied = false;
  /** The shore job in flight (its target — the watchdog's sliced redo bakes THERE, not at the stale centre). */
  private pendingShoreAt: { x: number; z: number } | null = null;
  private shoreJobsPosted = 0;
  private shoreRepliesDropped = 0;
  private static readonly LAKE_ENCODE_MAX = 8;
  private submerged = false;
  /** Submerged-camera screen effect (engine UnderwaterOverlay; eager + pre-warmed). */
  private overlay: UnderwaterOverlay | null = null;
  // ── Interactive water (2026-09-11): the wave sets the CPU height query
  // shares with the shaders, the wind's sea state (slewed), the interaction
  // field's rain debt and the splash emitter.
  private waves: GerstnerWave[] = [];
  private lakeWaves: GerstnerWave[] = [];
  /** The live modulation the sea material's uniforms carry (mutated in place; the foam grid reads it). */
  private readonly mod: WaveModulation = { ampScale: 1, dirRot: 0 };
  /** Lakes are sheltered: half the sea's amplitude response, the same heading. */
  private readonly lakeMod: WaveModulation = { ampScale: 1, dirRot: 0 };
  private seaState: SeaState = seaStateFor(0.45);
  private readonly rainDebt = { debt: 0 };
  private weather: WeatherModule | null = null;
  private splashEmitter: GpuParticleEmitter | CpuParticleAdapter | null = null;
  private readonly _splashPos = new THREE.Vector3();
  private readonly _windDrift = new THREE.Vector2(1, 0.8);
  /** W7 (2026-10-05): the tier's water-FX ruling, refreshed every frame (`water_interact.waterFxTier`). */
  private fxTier: WaterFxTier = waterFxTier('low', true, 0.25);
  /** W6: the drip emitter (small falling drops off a wet body) and its carry. */
  private dripEmitter: GpuParticleEmitter | CpuParticleAdapter | null = null;
  private readonly dripDebt = { debt: 0 };
  /** The tier / density the missing emitters were last tried for (a lazy build is attempted once per change). */
  private emitterTry = '';
  private dripSide = 0;
  /** W3/W4: other craft over the water — the nearest few, per-craft motion, the ring / wake gates, the spray carry. */
  private readonly craftNear: CraftReport[] = [];
  private readonly craftNearD: number[] = [];
  private readonly craftMotion = new Map<string, { x: number; z: number; t: number; vx: number; vz: number; debt: { debt: number }; angle: number }>();
  private readonly craftRingGate = new RateGate();
  private readonly craftWakeGate = new RateGate();
  private craftClockS = 0;
  private readonly craftDw = newDownwash();
  private craftN = 0;
  /** Keep the CRAFT_WATER_MAX nearest reports within range of the camera (insertion into a fixed-size sorted list). */
  private readonly collectCraft = (r: CraftReport): void => {
    const d = Math.hypot(r.x - this.camPos.x, r.z - this.camPos.z);
    if (d > CRAFT_WATER_RANGE_M) return;
    const near = this.craftNear, nearD = this.craftNearD;
    const n = this.craftN;
    if (n >= CRAFT_WATER_MAX && d >= nearD[CRAFT_WATER_MAX - 1]) return;
    let i = Math.min(n, CRAFT_WATER_MAX - 1);
    while (i > 0 && nearD[i - 1] > d) { near[i] = near[i - 1]; nearD[i] = nearD[i - 1]; i--; }
    near[i] = r; nearD[i] = d;
    if (n < CRAFT_WATER_MAX) this.craftN = n + 1;
  };
  private fxStats = { rainThisSecond: 0, stampsSeen: 0, seaAmp: 1, seaRot: 0, foamJ: 0.6, fieldOrigin: [0, 0] as [number, number] };
  // ── Water realism wave (2026-09-25) ──
  /** The look the bodies were built with (a quality change that moves it rebuilds them). */
  private look: WaterLook = waterLookFor({});
  private lookSig = '';
  /** R6: the sea bed's albedo (same lattice as the shore bake), sRGB. */
  private bedTex: THREE.DataTexture | null = null;
  private readonly bedBytes = new Uint8Array(SHORE_RES * SHORE_RES * 4);
  /** R1: the PMREM the bodies are bound to (identity-stable after the sky's first bake). */
  private boundEnv: THREE.Texture | null = null;
  private boundCloudMap: THREE.Texture | null = null;
  /** R12: the per-channel medium + marine snow / bubbles. */
  private medium: UnderwaterMedium | null = null;
  private life: UnderwaterLife | null = null;
  private readonly _inscatter = new THREE.Vector3();
  /** R17: splash crowns. */
  private crowns: SplashCrowns | null = null;
  // ── Shader gaps R1 (2026-09-29): water bodies ──
  /** The sea's VERTEX wave set (what the drawn mesh displaces — the waterline meets THAT surface). */
  private vertexWaves: GerstnerWave[] = [];
  /**
   * Shader gaps R5: the sea's waves behind ONE interface — the material, the foam memories, the caustics and every
   * height query read it. Gerstner (a pure wrapper: the old calls, bit for bit) or the FFT patch (ultra, WebGPU).
   */
  private seaWaves: WaveProvider | null = null;
  /** Why the sea is (not) the FFT one — and the decision the build took (a quality change that moves it rebuilds). */
  private fftReason: FftOceanReason = 'off-tier';
  private fftError = '';
  /** The per-point displacement scale the sea's queries use: the shoal (Gerstner — as before) or shoal × the horizon fade (FFT, per vertex). */
  private seaScaleAt: ((x: number, z: number) => number) | undefined = undefined;
  /** The labelled lake region and the body table the ground reads (null until packed). */
  private bodyLabels: LakeLabels | null = null;
  private bodyTable: BodyTable | null = null;
  private bodyPackAt: { x: number; z: number; region: LakeRegion | null; ms: number } | null = null;
  /** The body under the camera and the visual state of its waterline this frame. */
  private camBody: WaterBody | null = null;
  private wl: NearPlaneWaterline | null = null;
  private wlState: 'above' | 'below' | 'split' = 'above';
  private bodyCache: { x: number; z: number; kind: string | null; frames: number; body: WaterBody | null } | null = null;
  /** Probe A/B: true = compute the waterline but draw no veil / medium (`__waterBody().veil(false)`). */
  private probeVeilOff = false;
  /** The fog key last pushed to the atmosphere ('' = the above-water fog). */
  private fogKey = '';
  private readonly wlStats = { splitFrames: 0, belowFrames: 0, lastSurfaceY: NaN, lastLine: [0, 0, 0] as number[], xrWhole: false, body: '' as string };
  /** R15: which body renders its planar mirror this frame. */
  private planarBody: string | null = null;
  private planarTick = 0;
  private readonly lookStats = { rebuilds: 0, envBound: false, envIntensity: 1, cloudBound: false, farSea: false, planar: null as string | null, mediumVisible: false, inscatter: [0, 0, 0] as number[], maps: '' };

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    bus.on('atlas:world-entered', () => this.refresh());
    bus.on('atlas:environment-updated', () => this.refresh());
    // Water realism (2026-09-25): a graphics change that moves the water's look
    // rebuilds the bodies (their graphs are compiled per configuration).
    bus.on('atlas:quality-changed', () => this.onQualityChanged());
    app.world.addSystem(makeSystem('AtlasWater', Phase.RenderSub, 36, (dt) => this.tick(dt)));
    (globalThis as unknown as { __waterLook?: () => object }).__waterLook = () => this.lookState();
    // Shader gaps R5: the sea's wave provider — which, why, the patch's state, and a drawn-vs-continuous probe at a point.
    (globalThis as unknown as { __fftOcean?: (x?: number, z?: number) => object }).__fftOcean = (x?: number, z?: number) => this.fftOceanState(x, z);
    // Shader gaps R1: the body at the camera or a point, the packed table, the waterline (atlas:water-bodies = 'off' = the sea only).
    (globalThis as unknown as { __waterBody?: (x?: number, z?: number) => object }).__waterBody = (x?: number, z?: number) => this.waterBodyState(x, z);
    // Probe levers (the picture law): per-region channel means + a luminance row profile from ONE
    // rendered frame (the __framePixels readback), the near sea's rim on screen, the seaward heading.
    (globalThis as unknown as Record<string, unknown>).__waterFrame = (rects: Array<[number, number, number, number]>, band?: [number, number]) => this.frameRegions(rects, band);
    (globalThis as unknown as Record<string, unknown>).__waterSeam = () => this.seamScreen();
    (globalThis as unknown as Record<string, unknown>).__waterSeaward = () => this.seawardYaw();
    (globalThis as unknown as Record<string, unknown>).__waterSunYaw = () => { const d = uSunDir.value as THREE.Vector3; return Math.atan2(-d.x, -d.z); };
    // Probe diagnostics: submerge state + a deep-texel finder (world coords)
    // so a headless harness can teleport into real water deterministically.
    (globalThis as unknown as { __water?: () => object }).__water = () => ({
      lakeAt: (x: number, z: number): number | null => lakeSurfaceAtBaked(x, z), // the baked lake surface under a world point (probes)
      groundAt: (x: number, z: number): number | null => (this.seed ? meshHeight(this.seed, x, z) : null), // the drawn ground's height (probes, session 3)
      seaLevel: this.seaLevel,
      submerged: this.submerged,
      seaVisible: this.mesh?.visible ?? false,
      camY: Math.round(this.camPos.y * 100) / 100,
      depthHere: this.shoreline
        ? Math.round(this.shoreline.depthAt(this.camPos.x - this.centerX, this.camPos.z - this.centerZ) * 100) / 100
        : -1,
      center: [this.centerX, this.centerZ],
      maxDepth: this.shoreline ? Math.round(this.shoreline.maxDepth * 100) / 100 : -1,
      seaCull: { keptShare: this.seaCullKept, drawCount: this.mesh?.geometry.drawRange.count ?? -1 },
      overlayVisible: this.overlay?.mesh.visible ?? false,
      lakeVisible: this.lakeMesh?.visible ?? false,
      lakeSurfaceHere: lakeSurfaceAtBaked(this.camPos.x, this.camPos.z),
      reflection: this.reflectionState(),
      fluid: this.fluidState(),
      shore: {
        pending: this.pendingShoreId !== 0,
        jobsPosted: this.shoreJobsPosted,
        repliesDropped: this.shoreRepliesDropped,
        sliced: this.bakeTarget !== null,
        workerBroken: this.workerBroken,
        everReplied: this.bakeEverReplied,
      },
    });
    // Coast scanner: pure terrain math (no bake needed) — the nearest point
    // with a sea column ≥ `min` m within `radius` m of (x0, z0).
    (globalThis as unknown as { __waterScanCoast?: (x0: number, z0: number, radius?: number, min?: number) => number[] | null }).__waterScanCoast =
      (x0, z0, radius = 24_000, min = 4) => {
        if (!this.seed) return null;
        let best: number[] | null = null;
        let bestD = Infinity;
        for (let r = 1000; r <= radius; r += 1000) {
          for (let a = 0; a < 24; a++) {
            const ang = (a / 24) * Math.PI * 2;
            const x = x0 + Math.cos(ang) * r;
            const z = z0 + Math.sin(ang) * r;
            const depth = this.seaLevel - meshHeight(this.seed, x, z);
            if (depth >= min && r < bestD) { bestD = r; best = [x, z, Math.round(depth * 10) / 10]; }
          }
          if (best) break;
        }
        return best;
      };
    // Lake finder: deepest baked lake texel near the player (world coords).
    (globalThis as unknown as { __waterFindLake?: (min?: number) => number[] | null }).__waterFindLake = (min = 1.5) => {
      const n = LAKE_GRID_N;
      let best: number[] | null = null;
      let bestDepth = min;
      for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) {
          const d = this.lakeBytes[row * n + col] / 255 * AtlasWaterModule.LAKE_ENCODE_MAX;
          if (d > bestDepth) {
            bestDepth = d;
            best = [
              this.lakeCenterX + (col / (n - 1) - 0.5) * LAKE_REGION_SIZE,
              this.lakeCenterZ + (row / (n - 1) - 0.5) * LAKE_REGION_SIZE,
              Math.round(d * 10) / 10,
            ];
          }
        }
      }
      return best;
    };
    // `nearest` (session 3): the texel deeper than `min` NEAREST the camera instead of the deepest — a probe standing
    // 1.5 km off at the deepest texel re-centred the sea bake under itself and read 2 m of water mid-bake.
    (globalThis as unknown as { __waterFindDeep?: (min?: number, nearest?: boolean) => number[] | null }).__waterFindDeep = (min = 3, nearest = false) => {
      const n = SHORE_RES;
      let best: number[] | null = null;
      let bestDepth = min;
      let bestDist = Infinity;
      for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) {
          const depth = this.seaLevel - this.scratch[row * n + col];
          if (nearest) {
            if (depth <= min) continue;
            const x = this.centerX + (col / (n - 1) - 0.5) * FOOTPRINT, z = this.centerZ + (row / (n - 1) - 0.5) * FOOTPRINT;
            const d = Math.hypot(x - this.camPos.x, z - this.camPos.z);
            if (d < bestDist) { bestDist = d; best = [x, z, Math.round(depth * 10) / 10]; }
            continue;
          }
          if (depth > bestDepth) {
            bestDepth = depth;
            best = [
              this.centerX + (col / (n - 1) - 0.5) * FOOTPRINT,
              this.centerZ + (row / (n - 1) - 0.5) * FOOTPRINT,
              Math.round(depth * 10) / 10,
            ];
          }
        }
      }
      return best;
    };
  }

  /** Enter/leave the Commons: build or hide the sea. */
  private refresh(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const onCommons = atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    if (!onCommons) {
      if (this.mesh) this.mesh.visible = false;
      if (this.lakeMesh) this.lakeMesh.visible = false;
      this.leaveWater();
      setBedBodiesFrame(this.scene.position, false); // shader gaps R1: no Commons, no lake / river beds
      return;
    }
    // A primary-key find (the Commons id is known here) — the spread copied every world row each ping.
    const commons = atlas.commonsWorldId !== null ? conn.db.world.worldId.find(atlas.commonsWorldId) : undefined;
    if (!commons) return;
    if (!this.mesh || this.seed !== commons.terrainSeed) {
      this.build(commons.terrainSeed);
    } else {
      this.mesh.visible = this.shoreline !== null && this.shoreline.maxDepth > 0.05;
    }
  }

  /** Draw only the sea quads near wet ground (lib/sea_cull.ts): on a coast most of the 295k-triangle grid lies over
   *  land and ran the wave vertex stage for nothing. Rewrites the index in place after every shore bake (the scratch
   *  holds that bake's bed); `atlas:sea-cull` = 'off' keeps the whole grid. */
  private cullDrySea(): void {
    const geo = this.mesh?.geometry;
    const idx = geo?.index;
    if (!geo || !idx || !(idx.array instanceof Uint32Array)) return;
    let off = false;
    try { off = localStorage.getItem('atlas:sea-cull') === 'off'; } catch { /* storage denied: cull */ }
    const full = SEGMENTS * SEGMENTS * 6;
    const k = off ? buildSeaIndex(idx.array, SEGMENTS, this.scratch, SHORE_RES, this.seaLevel, Infinity, 0)
      : buildSeaIndex(idx.array, SEGMENTS, this.scratch, SHORE_RES, this.seaLevel);
    // A visible sea always keeps a quad (maxDepth > 0.05 means a wet bed); a zero count is only ever drawn hidden.
    geo.setDrawRange(0, Math.max(k, 3));
    idx.clearUpdateRanges();
    idx.addUpdateRange(0, Math.max(k, 3));
    idx.needsUpdate = true;
    this.seaCullKept = Math.round((k / full) * 1000) / 1000;
    if (k === 0 && this.mesh) this.mesh.visible = false;
  }

  /** Fill scratch rows [from, to) with bed heights around (cx, cz). */
  private fillScratchRows(cx: number, cz: number, from: number, to: number): void {
    const n = SHORE_RES;
    for (let row = from; row < to; row++) {
      const z = (row / (n - 1) - 0.5) * FOOTPRINT + cz;
      for (let col = 0; col < n; col++) {
        const x = (col / (n - 1) - 0.5) * FOOTPRINT + cx;
        this.scratch[row * n + col] = meshHeight(this.seed, x, z);
      }
    }
  }

  /** One-time build (per seed): waves, shoreline bake, material, mesh. */
  private build(seed: string): void {
    this.disposeWater();
    this.seed = seed;
    this.seaLevel = commonsSeaLevel(seed);

    // Deterministic modest swell — same sea for every client.
    // count 8 (the createWaveSet max): the two extra cascade components
    // (~1.3 m / 0.8 m) live purely in the per-pixel normal (below the vertex
    // cutoff) and break up the "moving blanket" read — real seas carry many
    // superimposed frequencies (GPU Gems ch.1 / FFT-ocean canon).
    const waves = createWaveSet({ amplitude: 0.4, wavelength: 22, count: 8, steepness: 0.5, seed: 41 });
    loopWaveSet(waves); // seamless at the clock's wrap (before any material or provider reads the set)
    this.waves = waves;
    this.vertexWaves = filterVertexWaves(waves, VERTEX_WAVE_CUTOFF);
    this.seaWaves = this.buildSeaWaves(waves);
    // ── Water realism (2026-09-25): the tier's look, the shared detail maps, the bed albedo ──
    const look = waterLookFor(activePreset());
    this.look = look;
    this.lookSig = waterLookSignature(activePreset());
    const maps = waterNormalMaps(look.spectrum);
    this.lookStats.maps = maps.source;
    if (look.bed) {
      // Neutral wet sand until the first bake lands (the worker bakes the real bed).
      for (let i = 0; i < SHORE_RES * SHORE_RES; i++) { this.bedBytes[i * 4] = 150; this.bedBytes[i * 4 + 1] = 135; this.bedBytes[i * 4 + 2] = 105; this.bedBytes[i * 4 + 3] = 255; }
      this.bedTex = new THREE.DataTexture(this.bedBytes, SHORE_RES, SHORE_RES, THREE.RGBAFormat);
      this.bedTex.wrapS = this.bedTex.wrapT = THREE.ClampToEdgeWrapping;
      this.bedTex.magFilter = THREE.LinearFilter;
      this.bedTex.minFilter = THREE.LinearFilter;
      this.bedTex.colorSpace = THREE.SRGBColorSpace;
      this.bedTex.needsUpdate = true;
    }
    const cloudMap = look.clouds ? this.cloudWeatherMap() : null;
    this.boundCloudMap = cloudMap;
    this.boundEnv = null;
    // Interactive water: the camera-following interaction field every water
    // material samples (built for the tier now; the graphics flag only gates
    // whether it ever steps — the LIVE toggle needs the texture bound).
    const fx = waterFx();
    ensureWaterField(activeTier());
    fx.enabled = activePreset().fluidPhysics !== false;
    fx.provider = this.surfaceProvider();
    // Stage 3: the foam trails ride the SAME wave set the surface displaces
    // with (the Jacobian of these waves is where crests fold); the caustics
    // projector warps the same grid by the refracted sun.
    if (activePreset().waterFoam) {
      try { this.foam = new FoamAccumulator(this.seaWaves ?? waves, { sizeX: FOOTPRINT, sizeZ: FOOTPRINT, resolution: 128, persistence: 2.5, jacobianFull: 0.35, jacobianStart: 0.75, updateHz: 20 }); this.foam.setModulation(this.mod); } catch { this.foam = null; }
    }
    // The sea-state uniforms are created HERE so the caustics projector
    // (built before the material — its light map is bound at material build)
    // refracts the same wind-modulated surface the sea draws.
    const seaUniforms = { ampScale: uniform(1), dirRot: uniform(0) };
    const seaModNodes = { ampScale: seaUniforms.ampScale, dirCos: cos(seaUniforms.dirRot), dirSin: sin(seaUniforms.dirRot) };
    // R10: half-metre foam trails over 256 m around the viewer, fed by the SAME waves and sea state
    // the surface draws (the whitecap Jacobian) and by the interaction field's wakes. The 128² CPU
    // grid above stays the far fallback — the material fades between them at the window's rim.
    if (look.foamField && this.foam) {
      // §3.3 (surfaces, 2026-09-27): the whitecaps age 2.5 s (Crest / HDRP: a foam age decaying over 1–3 s; the CPU trail's
      // own 2.5 s), the wakes keep the water agent's 6 s — two channels of the one field (FoamFieldConfig.wakeDecayS).
      try { this.foamField = new FoamField({ waves, waveProvider: this.seaWaves, waveModulation: seaModNodes as never, resolution: 512, size: 256, decayS: 2.5, wakeDecayS: 6, interaction: { texture: fx.texture, size: fx.size } }); } catch { this.foamField = null; }
    }
    if (activePreset().waterCaustics) {
      try { this.caustics = new CausticsProjector({ sizeX: FOOTPRINT, sizeZ: FOOTPRINT, floorDepth: 6, resolution: 256, segments: 96, strength: 1.4, waves, waveProvider: this.seaWaves, waveModulation: seaModNodes as never }); } catch { this.caustics = null; }
    }

    // Initial bake (world load): dry-probe first (the spawn city is dry by
    // the settlement clamp); a wet spawn bakes in the WORKER — the old
    // synchronous fill cost ~0.5 s of load time.
    this.scratch.fill(this.seaLevel + 1000); // bone dry until a bake lands
    if (this.probeWet(this.centerX, this.centerZ)) {
      const worker = this.ensureWorker();
      if (worker) {
        this.pendingShoreId = ++this.jobId;
        this.pendingShoreAt = { x: this.centerX, z: this.centerZ };
        this.shoreJobsPosted++;
        const job: WaterBakeJob = {
          kind: 'shore', id: this.pendingShoreId, seed, cx: this.centerX, cz: this.centerZ, res: SHORE_RES, size: FOOTPRINT,
          seaLevel: look.bed ? this.seaLevel : undefined,
        };
        this.bakeBeatMs = performance.now();
        worker.postMessage(job);
      } else {
        this.fillScratchRows(this.centerX, this.centerZ, 0, SHORE_RES);
      }
    }
    this.shoreline = new ShorelineField({
      sizeX: FOOTPRINT,
      sizeZ: FOOTPRINT,
      waterLevel: this.seaLevel,
      // The engine samples EXACTLY the scratch lattice positions during
      // rebake — recover the texel index and read the pre-filled grid.
      bedHeight: (x, z) => {
        const n = SHORE_RES;
        const col = Math.round(((x - this.centerX) / FOOTPRINT + 0.5) * (n - 1));
        const row = Math.round(((z - this.centerZ) / FOOTPRINT + 0.5) * (n - 1));
        if (col >= 0 && col < n && row >= 0 && row < n) return this.scratch[row * n + col];
        return meshHeight(this.seed, x, z);
      },
      resolution: SHORE_RES,
      shoalDepth: 7,
      // 0.45 m (2026-09-27, the coordinator's review — the water agent's own open item): foam lives in the SWASH
      // zone and on breaking crests; 1.4 m whitened every shallow flat to a sheet. The lake already sat at 0.45.
      // The shader breaks the band's edge with foamNoise (its mask) and the Jacobian whitecaps stay their own term.
      foamDepth: 0.45,
      offsetX: this.centerX,
      offsetZ: this.centerZ,
    });

    // R2: the shared detail maps (the session owns them — never disposed with a body).
    const normalMap = maps.choppy;
    this.normalMap = null;
    this.handles = createRealisticWaterMaterial(
      {
        ...DEFAULT_REALISTIC_WATER,
        normalMap,
        // WebGPU doctrine: no viewport scene copies — r182's mid-pass copy
        // corrupts encoder state (engine RealisticWaterMaterialConfig docs).
        screenSpaceRefraction: !(this.app?.renderer.isWebGPU ?? true),
        // Planar reflections are an extra scene render — heavy; OFF by
        // default, now a graphics-menu opt-in (applies on next world entry).
        reflection: activePreset().waterReflections ?? false,
        skyColor: 0x87a8c3,
        causticIntensity: 0.25,
        // De-geometrize the horizon: displacement flattens toward the plane
        // rim so the sheet's edge reads as haze, never a sawtooth silhouette.
        // The displacement is flat before the 1,536 m rim, where the far sea (R5) takes over.
        distanceFade: NEAR_SEA_FADE,
        // Anti-tiling: geometry carries only the swell the 8 m grid can
        // sample; the five shorter cascade waves stay per-pixel (see the
        // SEGMENTS comment).
        vertexWavelengthCutoff: VERTEX_WAVE_CUTOFF,
        shoreline: {
          depthTexture: this.shoreline.texture,
          encodeMax: this.shoreline.encodeMax,
          shoalDepth: this.shoreline.shoalDepth,
          foamDepth: this.shoreline.foamDepth,
          foamBandM: SURF_BAND_M, // surfaces (2026-09-27): the swash band ends 4 m out — a depth band was 10–20 m of white on a gentle beach
          shallowTint: 0xc2b280,
        },
        // Stage 3 textures (plane-UV addressed by the material; null = off).
        foamAccumTexture: this.foam ? this.foam.texture : null,
        foamField: this.foamField ? { texture: this.foamField.texture, size: this.foamField.size } : null,
        causticTexture: this.caustics ? this.caustics.texture : null,
        // Interactive water: the wind's sea state as uniforms (shared with
        // the caustics), the interaction field in world XZ, rain rings.
        seaState: seaUniforms,
        interaction: { texture: fx.texture, size: fx.size },
        // The tier's rain-ripple switch (it was hard-coded on — minimal/potato paid for rings they never asked for).
        rainRipples: activePreset().rainRipples === true,
        // R1–R6: the tier's water look (sky PMREM, clouds, spectrum detail, scattering, glitter filter, the bed).
        ...waterLookConfig('sea', look, maps, { bedTexture: this.bedTex, planeSize: { x: FOOTPRINT, z: FOOTPRINT }, cloudTexture: cloudMap }),
      },
      this.seaWaves ?? waves,
    );
    this.buildSplashEmitter();

    const geo = new THREE.PlaneGeometry(FOOTPRINT, FOOTPRINT, SEGMENTS, SEGMENTS);
    geo.rotateX(-Math.PI / 2);
    this.mesh = new THREE.Mesh(geo, this.handles.material);
    this.mesh.name = 'CommonsSea';
    this.mesh.frustumCulled = false; // displaced in the vertex stage
    this.mesh.position.set(this.centerX, this.seaLevel, this.centerZ);
    this.handles.uWorldOffset.value.set(this.centerX, this.centerZ);
    this.mesh.visible = this.shoreline.maxDepth > 0.05;
    this.cullDrySea();
    this.scene.add(this.mesh);
    // Planar reflections: three's reflector mirrors across the plane through
    // its TARGET (normal = the target's local +Z; the engine pre-rotates it so
    // that is the sheet's +Y). Seated under the mesh it follows every recenter
    // at sea level. Orphaned — as it was until 2026-09-10 — it sat at the FP64
    // rebase origin facing +Z: a VERTICAL mirror through the spawn point. The
    // player saw their own camera view stamped on the sea on one side of it
    // and black water (three clears a "facing away" reflection) on the other.
    if (this.handles.reflectorTarget) this.mesh.add(this.handles.reflectorTarget);

    // ── Lake layer: calm short waves, muddy shallow tint, own depth texture
    // (per-texel depth = surface − bed, so one mesh carries every lake in
    // the region at its own level).
    this.lakeDepthTex = new THREE.DataTexture(this.lakeBytes, LAKE_GRID_N, LAKE_GRID_N, THREE.RedFormat);
    this.lakeDepthTex.wrapS = THREE.ClampToEdgeWrapping;
    this.lakeDepthTex.wrapT = THREE.ClampToEdgeWrapping;
    this.lakeDepthTex.magFilter = THREE.LinearFilter;
    this.lakeDepthTex.minFilter = THREE.LinearFilter;
    this.lakeDepthTex.colorSpace = THREE.NoColorSpace;
    const lakeWaves = createWaveSet({ amplitude: 0.05, wavelength: 7, count: 4, steepness: 0.35, seed: 97 });
    loopWaveSet(lakeWaves); // the lakes run on the same wrapping clock
    this.lakeWaves = lakeWaves;
    this.lakeHandles = createRealisticWaterMaterial(
      {
        ...DEFAULT_REALISTIC_WATER,
        normalMap,
        // WebGPU doctrine: no viewport scene copies (see the sea material).
        screenSpaceRefraction: !(this.app?.renderer.isWebGPU ?? true),
        reflection: activePreset().waterReflections ?? false, // menu opt-in (next world entry)
        skyColor: 0x87a8c3,
        causticIntensity: 0.3,
        seaState: true,
        interaction: { texture: fx.texture, size: fx.size },
        rainRipples: activePreset().rainRipples === true,
        // Anti-tiling: the lake grid is 6 m cells and ALL four lake waves
        // (7 m cascade) sit below its Nyquist limit — displace none of them
        // (5 cm amplitude; the per-pixel normal carries the ripples).
        vertexWavelengthCutoff: 2.5 * (LAKE_REGION_SIZE / (LAKE_GRID_N - 1)),
        shoreline: {
          depthTexture: this.lakeDepthTex,
          encodeMax: AtlasWaterModule.LAKE_ENCODE_MAX,
          shoalDepth: 2.5,
          foamDepth: 0.45,
          foamBandM: SURF_BAND_M, // surfaces (2026-09-27): see the sea
          shallowTint: 0x9a8c66,
        },
        // R1/R2/R3/R4/R6/R13: the lake look (calm glass + cat's-paws, the calm spectrum map, a silty bed).
        ...waterLookConfig('lake', look, maps, { cloudTexture: cloudMap }),
      },
      lakeWaves,
    );
    // R5: the far tier's sea wears the same water (built here so it tracks the look; the far tier dresses its sea group with it).
    this.lookStats.farSea = !!farSeaHandles(look, waves);
    // Placeholder geometry: an empty BufferGeometry has no vertex layout for
    // pipeline creation — the pre-warm below needs a real (tiny) grid.
    const lakePlaceholder = new THREE.PlaneGeometry(1, 1, 1, 1);
    lakePlaceholder.rotateX(-Math.PI / 2);
    this.lakeMesh = new THREE.Mesh(lakePlaceholder, this.lakeHandles.material);
    this.lakeMesh.name = 'CommonsLakes';
    this.lakeMesh.frustumCulled = false;
    this.lakeMesh.visible = false; // until the first region bake lands
    this.scene.add(this.lakeMesh);
    // Same seating as the sea; the lake mesh sits at y = 0 with per-vertex
    // levels, so the target's local y is lifted to the nearest lake's level
    // every few frames (seatLakeMirror).
    if (this.lakeHandles.reflectorTarget) this.lakeMesh.add(this.lakeHandles.reflectorTarget);
    this.lakeCenterX = Infinity;
    this.lakeCenterZ = Infinity;

    // Submerged-camera screen effect — built NOW (not on first submerge) so
    // its pipeline can be pre-warmed below; first-use compilation at the
    // submerge moment was half of the 1–2 s hitch.
    this.overlay = new UnderwaterOverlay({ deepColor: 0x03212e, shallowColor: 0x2e8b8b });
    this.scene.add(this.overlay.mesh);
    if (activePreset().underwaterShafts) {
      try {
        this.shafts = createUnderwaterShafts({ count: 14, length: 40, width: 7, radius: 12, color: [0.5, 0.82, 0.92], intensity: 1, depthScale: 14 });
        this.shafts.mesh.visible = false;
        this.scene.add(this.shafts.mesh);
        stage4Stats.shaftsBuilt++;
      } catch { this.shafts = null; }
    }

    // R12: the per-channel medium (two screen passes, drawn only while submerged) + marine snow / bubbles.
    if (look.medium) {
      try { this.medium = new UnderwaterMedium(); for (const m of this.medium.meshes) this.scene.add(m); } catch { this.medium = null; }
    }
    if (look.marine && this.app) {
      try { this.life = new UnderwaterLife(this.app, this.scene); } catch { this.life = null; }
    }
    // R17: splash crowns ride every splash (the pooled emitter's hook).
    if (look.crowns) {
      try { this.crowns = new SplashCrowns(this.scene); } catch { this.crowns = null; }
    }
    this.lookStats.rebuilds++;
    // Shader gaps R1: the water-body descriptor's sources — the sea (the shoreline field, the drawn waves), the baked
    // lakes (while the lake layer shows) and the Commons rivers. Live closures: the fields they read move per frame.
    const seedNow = seed;
    setWaterBodySources({
      seed: seedNow,
      seaLevel: this.seaLevel,
      seaAt: (x, z) => !!this.mesh?.visible && this.shoreline !== null
        && Math.abs(x - this.centerX) <= FOOTPRINT * 0.5 && Math.abs(z - this.centerZ) <= FOOTPRINT * 0.5
        && this.shoreline.depthAt(x - this.centerX, z - this.centerZ) > 0.05,
      seaSurfaceAt: (x, z) => this.drawnSeaAt(x, z),
      lakeAt: (x, z) => ((this.lakeMesh?.visible ?? false) ? lakeSurfaceAtBaked(x, z) : null),
      riverAt: (x, z) => (atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId ? riverWaterAt(seedNow, x, z) : null),
    });

    // Pre-warm ALL water pipelines during world load (Renderer.compileAsync):
    // meshes flip visible with the overlay at zero intensity for the compile
    // pass (compile may skip invisibles), then restore.
    try {
      const native = this.app?.renderer.native as unknown as {
        compileAsync?: (o: unknown, c?: unknown, s?: unknown) => Promise<unknown>;
      };
      if (native?.compileAsync) {
        let camera: THREE.Camera | undefined;
        try {
          camera = this.app?.renderer.cameras.main;
        } catch {
          camera = undefined;
        }
        const overlay = this.overlay;
        overlay.uIntensity.value = 0; // effect invisible even while warming
        const meshes = [this.mesh, this.lakeMesh, overlay.mesh, ...(this.medium ? this.medium.meshes : [])];
        const restore = meshes.map((m) => m.visible);
        for (const m of meshes) m.visible = true;
        void Promise.allSettled(
          meshes.map((m) => native.compileAsync!(m, camera, this.scene)),
        ).then(() => {
          meshes.forEach((m, i) => { m.visible = this.submerged && (m === overlay.mesh || (this.medium?.meshes as THREE.Mesh[] | undefined)?.includes(m)) ? m.visible : restore[i]; });
          if (this.mesh && this.shoreline) this.mesh.visible = this.shoreline.maxDepth > 0.05;
          overlay.uIntensity.value = 1;
        });
      }
    } catch { /* renderer without compileAsync */ }
  }

  /** Swap the lake mesh over to a freshly baked region. */
  private applyLakeRegion(region: LakeRegion): void {
    if (!this.lakeMesh || !this.lakeHandles || !this.lakeDepthTex) return;
    this.lakeCenterX = region.centerX;
    this.lakeCenterZ = region.centerZ;
    if (region.maxDepth <= 0) {
      this.lakeMesh.visible = false;
      this.bodyLabels = null; // shader gaps R1: no lakes here — the body table drops them at its next pack
      setWaterBodyLabels(null);
      this.bodyPackAt = null;
      return;
    }
    const n = LAKE_GRID_N;
    // Per-texel depth drives shoaling/foam/tint in the shared water shader.
    for (let i = 0; i < n * n; i++) {
      const s = region.surface[i];
      const depth = Number.isNaN(s) ? 0 : Math.max(0, s - region.raw[i]);
      this.lakeBytes[i] = Math.round(Math.min(1, depth / AtlasWaterModule.LAKE_ENCODE_MAX) * 255);
    }
    this.lakeDepthTex.needsUpdate = true;

    // Region plane: wet nodes sit AT their lake surface; dry nodes sink just under the terrain (lib/drawn_water — the ONE
    // builder the waterline's `drawnLakeHeight` solves, so the lens and the picture agree to the millimetre).
    const geo = buildLakeSheetGeometry(region);
    const old = this.lakeMesh.geometry;
    this.lakeMesh.geometry = geo;
    disposeLater(old); // swapped inside tick(): the in-flight submit may still reference it
    this.lakeMesh.position.set(region.centerX, 0, region.centerZ);
    this.lakeHandles.uWorldOffset.value.set(region.centerX, region.centerZ);
    this.lakeMesh.visible = true;
    this.lakeRegion = region;
    this.seatLakeMirror();
    // Shader gaps R1: the region's lakes labelled for the descriptor + the ground's body table (re-packed next tick).
    this.bodyLabels = labelLakeRegion(region);
    setWaterBodyLabels(this.bodyLabels);
    this.bodyPackAt = null;
  }

  /**
   * Lift the lakes' mirror plane to the level of the lake nearest the camera.
   * One region mesh carries every lake at its own height (vertex y), so a
   * single planar reflector can only be exact for one of them — the one the
   * player is at or looking toward. Cheap (a 65×65 subsample), run every
   * 15 frames and on each region swap.
   */
  private seatLakeMirror(): void {
    const target = this.lakeHandles?.reflectorTarget;
    if (!target || !this.lakeRegion) return;
    const level = nearestLakeLevel(this.lakeRegion, this.camPos.x, this.camPos.z);
    if (level !== null) target.position.y = level;
  }

  /** Planar-reflection diagnostics for the probe (parts null when reflections are off). */
  /** The interactive-water state for probes (`__water().fluid`). */
  private fluidState(): object {
    const fx = waterFx();
    return {
      enabled: fx.enabled, tier: activeTier(), field: fx.spec, fieldActive: fx.field?.isActive ?? false, origin: this.fxStats.fieldOrigin,
      stamps: fx.stats.stamps, rain: fx.stats.rain, steps: fx.stats.steps, splashes: fx.stats.splashes,
      seaAmp: Math.round(this.fxStats.seaAmp * 1000) / 1000, seaRotDeg: Math.round((this.fxStats.seaRot * 180) / Math.PI * 10) / 10,
      foamJ: Math.round(this.fxStats.foamJ * 1000) / 1000, target: this.seaState,
      rainIntensity: this.handles ? Math.round(this.handles.uRainIntensity.value * 100) / 100 : 0,
      drift: this.handles ? [Math.round(this.handles.uDetailDrift.value.x * 100) / 100, Math.round(this.handles.uDetailDrift.value.y * 100) / 100] : null,
      splashEmitter: this.splashEmitter ? this.splashEmitter.backend : null,
      // W1–W7 (2026-10-05): the interaction counters, the tier ruling, the wetness, the craft on the water now.
      interact: { ...fx.interact, particles: fx.particles, particleScale: Math.round(fx.particleScale * 100) / 100, ruling: { ...this.fxTier }, dripEmitter: this.dripEmitter ? this.dripEmitter.backend : null, wet: { ...fx.wet }, craft: fx.craft.list.slice(0, fx.craft.count).map((c) => ({ ...c })) },
      // R10: the GPU foam memory (null below high / without the foam trails).
      foamField: this.foamField ? { size: this.foamField.size, resolution: this.foamField.resolution, steps: this.foamField.stats.steps, reorigins: this.foamField.stats.reorigins, center: [(this.foamField.uCenter as unknown as { value: THREE.Vector2 }).value.x, (this.foamField.uCenter as unknown as { value: THREE.Vector2 }).value.y] } : null,
      // R10: the foam field's own coverage — a readback of its newest target (lace, never a wash).
      foamStats: (): Promise<object | null> => this.readFoamStats(),
      // Probe helpers: stamp and read at ABSOLUTE coordinates.
      heightAt: (x: number, z: number): number => fx.field?.heightAtWorld(x, z) ?? 0,
      liveAt: (x: number, z: number): number | null => fx.provider?.liveAt(x, z) ?? null,
      stillAt: (x: number, z: number): number | null => fx.provider?.stillAt(x, z) ?? null,
      disturb: (x: number, z: number, strength = -0.05, radius = 0.5): boolean => { if (!fx.enabled || !fx.field) return false; fx.field.disturbWorld(x, z, strength, radius); fx.stats.stamps++; return true; },
      splash: (x: number, y: number, z: number, count = 40): boolean => { if (!fx.splash || !fx.enabled) return false; fx.splash(x, y, z, count, 3); fx.stats.splashes++; return true; },
      // W4 probe lever: report a craft through THE feed every craft module uses (silent — no engine voice), absolute
      // coordinates; call it each frame for a craft that stays (a report is a 700 ms lease).
      craftAt: (kind: 'hover' | 'ship' | 'freighter', x: number, y: number, z: number, hullM = 0): void => {
        reportCraft(`probe:${kind}`, kind, x, y, z, performance.now(), { hullM, silent: true });
      },
    };
  }

  /**
   * R10 probe lever (2026-09-25, session 2): read the newest foam target back and
   * summarise it — the mean, the peak and the share of texels over 0.03 / 0.5. A
   * saturated field would shade the whole window as a white wash; this is the
   * number that says it is not (the step counter says nothing about the picture).
   */
  private async readFoamStats(): Promise<object | null> {
    const f = this.foamField;
    if (!f || !this.app) return null;
    const n = f.resolution;
    const native = this.app.renderer.native as unknown as { readRenderTargetPixelsAsync(rt: unknown, x: number, y: number, w: number, h: number): Promise<ArrayLike<number>> };
    // THE READBACK LAW: WebGPU pads rows to 256 B and hands the padded buffer back — tighten before indexing.
    const px = tightenReadback((await native.readRenderTargetPixelsAsync(f.renderTarget, 0, 0, n, n)) as ArrayLike<number> & { constructor: { name: string } }, n, n);
    const half = px instanceof Uint16Array;
    const byte = px instanceof Uint8Array || px instanceof Uint8ClampedArray;
    let sum = 0, max = 0, c03 = 0, c50 = 0;
    const count = n * n;
    for (let i = 0; i < count; i++) {
      const raw = Math.max(px[i * 4], px[i * 4 + 1]); // R whitecaps, G wakes (surfaces 2026-09-27) — a half's bits order like its value for v ≥ 0
      const v = half ? THREE.DataUtils.fromHalfFloat(raw) : byte ? raw / 255 : raw;
      sum += v; if (v > max) max = v; if (v > 0.03) c03++; if (v > 0.5) c50++;
    }
    const r = (v: number): number => Math.round(v * 10000) / 10000;
    return { mean: r(sum / count), max: r(max), cover03: r(c03 / count), cover50: r(c50 / count), texels: count, type: (px as { constructor: { name: string } }).constructor.name };
  }

  private reflectionState(): object {
    const describe = (h: RealisticWaterHandles | null, mesh: THREE.Mesh | null): object | null => {
      if (!h || !h.reflectorTarget || !mesh) return null;
      const t = h.reflectorTarget;
      t.updateWorldMatrix(true, false);
      this._mirrorN.set(0, 0, 1).transformDirection(t.matrixWorld); // three's reflector normal: the target's local +Z
      t.getWorldPosition(this._mirrorP);
      const base = h.reflectorNode?.reflector as unknown as {
        hasOutput?: boolean;
        bounces?: boolean;
        virtualCameras?: WeakMap<object, THREE.Camera>;
        renderTargets?: Map<object, { width: number; height: number }>;
      } | undefined;
      // The virtual camera is repositioned ONLY inside a reflection render
      // (ReflectorNode.updateBefore) — `hasOutput` alone can be left true by
      // the boot pre-warm. Its y must mirror the camera's under the plane.
      let virtualCamY: number | null = null;
      let rt: string | null = null;
      try {
        const cam = this.app?.renderer.cameras.main;
        if (!cam) throw new Error("no camera");
        const vc = base?.virtualCameras?.get(cam);
        if (vc) {
          virtualCamY = Math.round((vc.position.y - this.scene.position.y) * 100) / 100;
          const target = base?.renderTargets?.get(vc);
          if (target) rt = `${target.width}x${target.height}`;
        }
      } catch { /* no main camera yet */ }
      return {
        parented: t.parent === mesh,
        normalY: Math.round(this._mirrorN.y * 1000) / 1000,
        planeY: Math.round((this._mirrorP.y - this.scene.position.y) * 100) / 100, // absolute (the scene carries the rebase shift)
        hasOutput: base?.hasOutput ?? null,
        bounces: base?.bounces ?? null,
        virtualCamY,
        rt,
      };
    };
    return {
      on: activePreset().waterReflections ?? false,
      sea: describe(this.handles, this.mesh),
      lake: describe(this.lakeHandles, this.lakeMesh),
    };
  }

  /** Advance the sliced lake bake (runs only when the sea bake is idle). */
  private stepLakeBake(): void {
    if (!this.lakeBake) return;
    const ROWS_PER_FRAME = 2;
    this.lakeBakeRow = this.lakeBake.fillRows(this.lakeBakeRow, this.lakeBakeRow + ROWS_PER_FRAME);
    if (this.lakeBakeRow < LAKE_GRID_N) return;
    const region = this.lakeBake.finalize();
    this.lakeBake = null;
    this.applyLakeRegion(region);
  }

  /** Lazily build the bake worker; sliced fallback when unavailable. */
  private ensureWorker(): Worker | null {
    if (this.workerBroken) return null;
    if (this.bakeWorker) return this.bakeWorker;
    try {
      this.bakeWorker = new Worker(new URL('../workers/water_bake_worker.ts', import.meta.url), { type: 'module' });
      if (this.seed) postRiverTable(this.bakeWorker, this.seed); // shared network (perf frontier item 12)
      this.bakeWorker.onerror = () => {
        this.workerBroken = true;
        this.bakeWorker?.terminate();
        this.bakeWorker = null;
      };
      this.bakeWorker.onmessage = (e: MessageEvent<WaterBakeResult>) => {
        this.bakeBeatMs = performance.now();
        this.bakeEverReplied = true;
        this.onBakeResult(e.data);
      };
    } catch {
      this.workerBroken = true;
      this.bakeWorker = null;
    }
    return this.bakeWorker;
  }

  /** Apply a worker bake (stale responses from superseded targets dropped). */
  private onBakeResult(r: WaterBakeResult): void {
    if (r.kind === 'shore') {
      if (r.id !== this.pendingShoreId || !this.mesh || !this.shoreline || !this.handles) { this.shoreRepliesDropped++; return; }
      this.pendingShoreId = 0;
      this.pendingShoreAt = null;
      this.scratch.set(r.heights);
      if (r.albedo && this.bedTex) { this.bedBytes.set(r.albedo); this.bedTex.needsUpdate = true; }
      this.centerX = r.cx;
      this.centerZ = r.cz;
      this.shoreline.rebake(r.cx, r.cz);
      this.mesh.position.set(r.cx, this.seaLevel, r.cz);
      this.handles.uWorldOffset.value.set(r.cx, r.cz);
      this.mesh.visible = this.shoreline.maxDepth > 0.05;
      this.cullDrySea();
      return;
    }
    if (r.kind !== 'lake' || r.id !== this.pendingLakeId) return;
    this.pendingLakeId = 0;
    const region: LakeRegion = {
      centerX: r.centerX,
      centerZ: r.centerZ,
      raw: r.raw,
      surface: r.surface,
      maxDepth: r.maxDepth,
    };
    insertLakeRegion(region);
    this.applyLakeRegion(region);
  }

  /** 8×8 dry-probe: fully-dry footprints skip the fine bake entirely. */
  private probeWet(x: number, z: number): boolean {
    for (let r = 0; r < 8; r++) {
      const pz = (r / 7 - 0.5) * FOOTPRINT + z;
      for (let c = 0; c < 8; c++) {
        const px = (c / 7 - 0.5) * FOOTPRINT + x;
        if (meshHeight(this.seed, px, pz) < this.seaLevel + 2) return true;
      }
    }
    return false;
  }

  /** Queue a recenter — worker bake, or sliced rows when no Worker exists. */
  private requestRecenter(x: number, z: number): void {
    if (!this.mesh || !this.shoreline || !this.handles) return;
    const sx = Math.round(x / SNAP) * SNAP;
    const sz = Math.round(z / SNAP) * SNAP;
    if (sx === this.centerX && sz === this.centerZ) return;
    if (this.bakeTarget && this.bakeTarget.x === sx && this.bakeTarget.z === sz) return;

    // Inland travel (the common case): just move the hidden footprint. The
    // probe (64 uncached terrain samples) is memoized per snap point: while a
    // shore bake was in flight the distance check re-entered EVERY tick and
    // re-probed the same point — 4–17 ms a frame for the whole bake (2026-09-12).
    let wet: boolean;
    if (this.lastProbe && this.lastProbe.x === sx && this.lastProbe.z === sz) wet = this.lastProbe.wet;
    else { wet = this.probeWet(sx, sz); this.lastProbe = { x: sx, z: sz, wet }; }
    if (!wet) {
      this.bakeTarget = null;
      this.pendingShoreId = 0;
      this.centerX = sx;
      this.centerZ = sz;
      this.mesh.position.set(sx, this.seaLevel, sz);
      this.handles.uWorldOffset.value.set(sx, sz);
      this.mesh.visible = false;
      return;
    }

    const worker = this.ensureWorker();
    if (worker) {
      // ONE shore job in flight. The distance check fires every tick until a
      // reply moves the centre; posting a fresh job each tick (a new id, the
      // older replies dropped as stale) queued a 320² bake per FRAME behind a
      // worker that finishes one every few hundred ms — the newest reply never
      // came, the sea never re-baked after a teleport or a long ride, and the
      // per-dispatch heartbeat kept the stall watchdog asleep (2026-09-10).
      // The reply re-arms the distance check if the player is still far.
      if (this.pendingShoreId !== 0) return;
      this.pendingShoreId = ++this.jobId;
      this.pendingShoreAt = { x: sx, z: sz };
      this.shoreJobsPosted++;
      const job: WaterBakeJob = {
        kind: 'shore', id: this.pendingShoreId, seed: this.seed, cx: sx, cz: sz, res: SHORE_RES, size: FOOTPRINT,
        seaLevel: this.look.bed ? this.seaLevel : undefined,
      };
      this.bakeBeatMs = performance.now();
      worker.postMessage(job);
      return;
    }
    this.bakeTarget = { x: sx, z: sz };
    this.bakeRow = 0;
  }

  /** FALLBACK sliced bake (no Worker); the dry-probe ran at request time. */
  private stepBake(): void {
    if (!this.bakeTarget || !this.mesh || !this.shoreline || !this.handles) return;
    const ROWS_PER_FRAME = 3; // ~43 frames per recenter, a few ms each
    const { x, z } = this.bakeTarget;
    const end = Math.min(SHORE_RES, this.bakeRow + ROWS_PER_FRAME);
    // Rows are sampled around the TARGET center; the closure maps via
    // this.centerX/Z, which flips to the target just before the rebake.
    this.fillScratchRows(x, z, this.bakeRow, end);
    this.bakeRow = end;
    if (end < SHORE_RES) return;
    this.centerX = x;
    this.centerZ = z;
    this.shoreline.rebake(x, z);
    this.mesh.position.set(x, this.seaLevel, z);
    this.handles.uWorldOffset.value.set(x, z);
    // A fully dry footprint (inland) renders nothing at all.
    this.mesh.visible = this.shoreline.maxDepth > 0.05;
    this.cullDrySea();
    this.bakeTarget = null;
  }

  private tick(dt: number): void {
    if (!this.mesh || !this.handles || !this.app) return;
    if (atlas.currentWorldId !== atlas.commonsWorldId) return;

    // One clock for the shaders; sun follows the sky module's uniform.
    this.time = (this.time + dt) % WATER_CLOCK_WRAP_S; // seamless: every φ is a whole number of turns per wrap (loopWaveSet)
    this.handles.uTime.value = this.time;
    // The wave field is evaluated one frame back for positionPrevious — feed
    // it the REAL delta so the velocity buffer matches a variable frame rate.
    this.handles.uFrameDt.value = Math.min(0.1, Math.max(1 / 240, dt));
    // Stage 3: refresh the foam grid at its own cadence (20 Hz, decay exact)
    // over the sea's current centre; render the caustics every other frame
    // (its own RT, before the main pass — this tick is RenderSub/36).
    if (this.foam && this.mesh.visible && this.foam.update(this.time, dt, this.centerX, this.centerZ)) stage3Stats.foamUpdates++;
    if (this.caustics && this.mesh.visible && (++this.causticFrame & 1) === 0) {
      try { this.caustics.render(this.app.renderer.native as never, this.time); stage3Stats.causticRenders++; } catch { this.caustics = null; }
    }
    this.tickFluid(dt);
    this.stepSeaWaves(dt);
    this.stepFoamField(dt);
    // sky/post (2026-09-27, THE MOON): the KEY's direction — the moon at night (its road on the sea), with uSunLight its light.
    this.handles.uSunDirection.value.copy(lightFrame.valid ? (lightFrame.keyDir as unknown as THREE.Vector3) : (uSunDir.value as THREE.Vector3));
    // Authored water radiance follows the PHOTOMETRIC CURVE the ground is lit by (session 3, 2026-09-26: the
    // lighting agent's recipe, rule 3 — lightFrame.keyScene / skyScene): the diffuse terms by the horizontal global,
    // the glint / scattering / bed by the key itself (uSunLight). ONE rule for sea, lakes, rivers (lib/water_lighting).
    const sunY = (uSunDir.value as THREE.Vector3).y;
    const light = waterLight(lightFrame, sunY, this._light);
    const illum = light.illum;
    this.handles.uIlluminance.value = illum;
    this.handles.uSunLight.value.setRGB(light.sun.r, light.sun.g, light.sun.b);
    // Sky-side tint tracks the hour (reflection is off — the fallback is a
    // uniform): warm low-sun band around dawn/dusk, deep navy at night.
    waterSkyTint(sunY, this._skyTint);
    this.handles.uSkyColor.value.copy(this._skyTint);
    if (this.lakeHandles) {
      this.lakeHandles.uTime.value = this.time;
      this.lakeHandles.uFrameDt.value = Math.min(0.1, Math.max(1 / 240, dt));
      this.lakeHandles.uSunDirection.value.copy(this.handles.uSunDirection.value); // sky/post (2026-09-27): the key (the moon at night)
      this.lakeHandles.uIlluminance.value = illum;
      this.lakeHandles.uSunLight.value.copy(this.handles.uSunLight.value);
      this.lakeHandles.uSkyColor.value.copy(this._skyTint);
    }
    this.tickLook(dt, illum);

    let camera: THREE.Camera | undefined;
    try {
      camera = this.app.renderer.cameras.main;
    } catch {
      camera = undefined;
    }
    if (!camera) return;
    camera.getWorldPosition(this.camPos);
    // FP64 origin rebase (AtlasOriginRebaseModule) holds scene.position at
    // MINUS-the-origin while children carry ABSOLUTE coords — so a world-space
    // camera read comes back origin-shifted. Convert back to absolute before
    // any recenter math or terrainHeight sampling, or the first rebase beyond
    // 10 km re-bakes the sea/lakes at the wrong spot and hides them (the
    // "water vanished after a second at the ocean" bug).
    this.camPos.x -= this.scene.position.x;
    this.camPos.z -= this.scene.position.z;

    // Wave phase must match the FRAGMENT stage, which evaluates normals/foam
    // from positionWorld — origin-shifted under the rebase. Feed uWorldOffset
    // in the SAME rebased space every frame (phase re-anchors once per 10 km
    // rebase — the same accepted nit as the ground macro noise).
    this.handles.uWorldOffset.value.set(
      this.centerX + this.scene.position.x,
      this.centerZ + this.scene.position.z,
    );
    if (this.lakeHandles && Number.isFinite(this.lakeCenterX)) {
      this.lakeHandles.uWorldOffset.value.set(
        this.lakeCenterX + this.scene.position.x,
        this.lakeCenterZ + this.scene.position.z,
      );
    }

    // Stall watchdog: a bake worker wedged mid-import (cold dev-server
    // dep-optimize) fires no 'error' — the lake gate (`!pendingLakeId`) would
    // block forever and a spawn shore bake would leave the sea bone-dry.
    // Silence with a job in flight for 20 s ⇒ broken; sliced fallbacks resume.
    // BOOT vs stall budgets (the chunk worker's law): a cold worker legitimately
    // takes tens of seconds to import its graph on a dev server — 20 s there
    // abandoned a healthy worker for the session ("bake worker stalled — sliced
    // main-thread bakes take over" on a real machine, perf sweep 2026-08-22).
    const stallBudget = this.bakeEverReplied ? 20_000 : 90_000;
    if ((this.pendingShoreId !== 0 || this.pendingLakeId !== 0) && performance.now() - this.bakeBeatMs > stallBudget) {
      this.workerBroken = true;
      this.bakeWorker?.terminate();
      this.bakeWorker = null;
      if (this.pendingShoreId !== 0) {
        this.pendingShoreId = 0;
        // Redo the LOST bake, sliced — at the target it was for, not at the
        // stale centre (which sliced a footprint the player had already left).
        this.bakeTarget = this.pendingShoreAt ?? { x: this.centerX, z: this.centerZ };
        this.pendingShoreAt = null;
        this.bakeRow = 0;
      }
      if (this.pendingLakeId !== 0) {
        this.pendingLakeId = 0;
        this.lakeCenterX = Infinity; // re-arm the lake gate for the sliced path
        this.lakeCenterZ = Infinity;
      }
      console.warn('[AtlasWater] bake worker stalled — sliced main-thread bakes take over');
    }

    if (
      Math.abs(this.camPos.x - this.centerX) > REBAKE_DIST ||
      Math.abs(this.camPos.z - this.centerZ) > REBAKE_DIST
    ) {
      this.requestRecenter(this.camPos.x, this.camPos.z);
    }

    // Lake region follows the player too (quarter-region hysteresis) —
    // baked in the worker; sliced main-thread only as the fallback.
    if (
      !this.lakeBake &&
      !this.pendingLakeId &&
      (Math.abs(this.camPos.x - this.lakeCenterX) > LAKE_REGION_SIZE / 4 ||
        Math.abs(this.camPos.z - this.lakeCenterZ) > LAKE_REGION_SIZE / 4)
    ) {
      const sx = Math.round(this.camPos.x / SNAP) * SNAP;
      const sz = Math.round(this.camPos.z / SNAP) * SNAP;
      const worker = this.ensureWorker();
      if (worker) {
        this.pendingLakeId = ++this.jobId;
        // Optimistically mark the region as current so the gate doesn't
        // re-fire every tick while the worker runs; a supersede re-posts.
        this.lakeCenterX = sx;
        this.lakeCenterZ = sz;
        const job: WaterBakeJob = { kind: 'lake', id: this.pendingLakeId, seed: this.seed, cx: sx, cz: sz };
        this.bakeBeatMs = performance.now();
        worker.postMessage(job);
      } else {
        this.lakeBake = createLakeBake(this.seed, sx, sz);
        this.lakeBakeRow = 0;
      }
    }
    if (this.bakeTarget) this.stepBake();
    else this.stepLakeBake();
    if (++this.mirrorTick % 15 === 0) this.seatLakeMirror();

    // Submerged? (shader gaps R1, 2026-09-29) WHICH body lies over the camera (lib/water_body — a lake, a river, the
    // sea), the DRAWN surface there, and the near-plane waterline against it — see stepUnderwater.
    this.stepUnderwater(camera, dt);
  }

  /**
   * The underwater camera (shader gaps R1, 2026-09-29). The body over the camera (a baked lake, then a river channel,
   * then the sea — the surface provider's order); its DRAWN surface at the camera (the sea: its VERTEX waves × the
   * shoal + the interaction ripple — the geometry the lens meets, not the per-pixel chop; lakes and rivers are flat
   * sheets); and the near-plane waterline (engine waterLook.nearPlaneWaterline):
   *   'below' — every near-plane corner under the water: today's whole-view look (fog, veil, medium, shafts), in THIS
   *             body's optics;
   *   'split' — the surface cuts the lens: the medium and the veil only under the line, the meniscus along it, the
   *             above-water fog and no shafts;
   *   'above' — nothing.
   * The ORIGIN's side (the ear — atlas.submerged, the splash, the marine life) is judged against the same surface.
   * XR presenting keeps the whole-view switch (one line cannot serve two eyes). `atlas:water-bodies = 'off'` restores
   * the sea-only selection verbatim (the origin against the flat level, the sea's optics everywhere).
   */
  private stepUnderwater(camera: THREE.Camera, dt: number): void {
    const cx = this.camPos.x, cz = this.camPos.z;
    const bodiesOn = waterBodiesEnabled();
    // The pre-R1 selection, verbatim — the 'off' switch runs it alone, the descriptor path keeps it as the gate.
    const overWater =
      !!this.mesh?.visible &&
      this.shoreline !== null &&
      this.shoreline.depthAt(cx - this.centerX, cz - this.centerZ) > 0.05;
    const riverSurface = this.seed ? riverWaterAt(this.seed, cx, cz) : null;
    // A river channel counts as a lake at its water level (terrain wave 2026-09-08).
    const lakeSurface = lakeSurfaceAtBaked(cx, cz) ?? riverSurface;
    // Symmetric with the sea: a hidden lake mesh is no lake (the perf harness
    // surfaces a probe by hiding the water meshes; lakes landed after that trick).
    const lakeOk = ((this.lakeMesh?.visible ?? false) || riverSurface !== null) && lakeSurface !== null;
    let kind: 'sea' | 'lake' | 'river' | null = lakeOk ? (lakeSurfaceAtBaked(cx, cz) !== null ? 'lake' : 'river') : overWater ? 'sea' : null;
    let body: WaterBody | null = null;
    let surfaceY = kind === 'sea' ? this.seaLevel : kind ? (lakeSurface as number) : NaN;
    let originUnder: boolean;
    let state: 'above' | 'below' | 'split';
    let line: [number, number, number] | null = null;
    if (!bodiesOn || !kind) {
      originUnder = kind === 'sea' ? this.camPos.y < this.seaLevel : kind ? this.camPos.y < surfaceY - 0.05 : false;
      state = originUnder ? 'below' : 'above';
    } else {
      // The descriptor, cached: re-asked when the kind under the camera changes, after 4 m, or every 30 frames.
      const bc = this.bodyCache;
      if (!bc || bc.kind !== kind || Math.hypot(cx - bc.x, cz - bc.z) > 4 || ++bc.frames > 30) {
        this.bodyCache = { x: cx, z: cz, kind, frames: 0, body: bodyAt(cx, cz) };
      }
      body = this.bodyCache!.body;
      if (body && body.kind !== kind) kind = body.kind;
      // The DRAWN surface and its slope (finite differences over ±0.5 m — the lens is 7 cm across, a wave metres).
      let slopeX = 0, slopeZ = 0;
      if (kind === 'sea') {
        surfaceY = this.drawnSeaAt(cx, cz);
        slopeX = (this.drawnSeaAt(cx + 0.5, cz) - this.drawnSeaAt(cx - 0.5, cz)) / 1;
        slopeZ = (this.drawnSeaAt(cx, cz + 0.5) - this.drawnSeaAt(cx, cz - 0.5)) / 1;
      } else {
        // THE DRAWN BODY (2026-09-30, the waterline's δ): a lake is its region mesh's own triangle — a node by a dry shore
        // is sunk under the terrain, so the sheet tilts (measured ~5 mm under the level at a lake's lens) — and a river is
        // its ribbon, drawn RIVER_RIBBON_LIFT over the water; + the interaction ripple. The slope is the drawn one too.
        const level = body && body.kind === kind ? body.level : (lakeSurface as number);
        const field = waterFx().field;
        const drawnAt = (x: number, z: number): number => {
          const base = kind === 'lake'
            ? ((this.lakeMesh?.visible && this.lakeRegion ? drawnLakeHeight(this.lakeRegion, x, z) : null) ?? level)
            : (this.seed ? riverWaterAt(this.seed, x, z) : null) ?? level;
          return base + (kind === 'river' ? RIVER_RIBBON_LIFT : 0) + (field?.heightAtWorld(x, z) ?? 0);
        };
        surfaceY = drawnAt(cx, cz);
        slopeX = drawnAt(cx + 0.5, cz) - drawnAt(cx - 0.5, cz);
        slopeZ = drawnAt(cx, cz + 0.5) - drawnAt(cx, cz - 0.5);
      }
      originUnder = kind === 'sea' ? this.camPos.y < surfaceY : this.camPos.y < surfaceY - 0.05;
      const xr = (this.app?.renderer.native as unknown as { xr?: { isPresenting?: boolean } } | undefined)?.xr?.isPresenting ?? false;
      this.wlStats.xrWhole = xr;
      if (xr || !(camera as THREE.PerspectiveCamera).isPerspectiveCamera) {
        state = originUnder ? 'below' : 'above';
      } else {
        const wl = waterlineFor(camera as THREE.PerspectiveCamera, this.camPos, surfaceY, slopeX, slopeZ);
        this.wl = wl;
        state = wl.state;
        line = wl.line;
        this.wlStats.lastLine = wl.line.map((v) => Math.round(v * 1e4) / 1e4);
      }
    }
    this.camBody = body;
    this.wlState = state;
    this.wlStats.lastSurfaceY = Number.isFinite(surfaceY) ? Math.round(surfaceY * 1000) / 1000 : NaN;
    this.wlStats.body = kind ?? '';
    if (state === 'split') this.wlStats.splitFrames++;
    else if (state === 'below') this.wlStats.belowFrames++;

    // ── the ORIGIN (the ear): the splash, the shared flag, the marine life ──
    if (originUnder && !this.submerged) {
      this.life?.dive(this.camPos.x, this.camPos.y, this.camPos.z);
      sfx('sfx_water_splash', { gain: 0.5, cooldownMs: 600 }); // breaking the surface
      this.submerged = true;
    } else if (!originUnder && this.submerged) {
      this.submerged = false;
      atlas.submerged = false;
    }
    if (originUnder) atlas.submerged = true; // re-assert each frame: a water DEVICE leaving its volume writes false on its own transition
    if (this.life) this.life.update(dt, originUnder, this.camPos, originUnder ? surfaceY : null, this.scene.position);

    // ── the VIEW ── (`probeVeilOff`: the probe's A/B lever — the states are computed, the veil and the medium not drawn)
    if (state === 'above' || this.probeVeilOff) { this.hideUnderwaterView(); this.lookStats.mediumVisible = false; return; }
    const optics = bodiesOn && kind ? OPTICS_BY_BODY[kind] : OPTICS_BY_BODY.sea;
    const k = bodiesOn && kind === 'lake' ? (body?.turbidity ?? 1) : 1;
    const depth = surfaceY - this.camPos.y;
    // The fog carries the underwater look only with the WHOLE lens under (a split keeps the air's fog above the line).
    const fogKey = state === 'below' ? `${bodiesOn && kind ? kind : 'sea'}:${this.medium ? 1 : 0}:${k.toFixed(2)}` : '';
    if (fogKey !== this.fogKey) {
      if (!fogKey) setUnderwaterFog(null);
      else if (optics === OPTICS_BY_BODY.sea) {
        // Fog goes underwater by MUTATING the atmosphere's own FogExp2 — swapping the scene.fog object rebuilt every
        // material (the 1–2 s submerge/surface hitch). R12: with the per-channel medium the monochrome fog only hazes
        // the far sky (it would absorb twice).
        setUnderwaterFog({ color: 0x06222e, density: this.medium ? 0.006 : 0.045 });
      } else {
        setUnderwaterFog({ color: optics.fogColor, density: this.medium ? 0.006 : optics.fogDensity * k });
      }
      this.fogKey = fogKey;
    }
    const split = state === 'split' ? line : null;
    if (this.overlay) {
      const o = this.overlay;
      o.mesh.visible = true;
      o.uIntensity.value = this.medium ? 0.3 : 1;
      o.uDeepColor.value.set(optics.overlay.deep);
      o.uShallowColor.value.set(optics.overlay.shallow);
      const ab = k === 1 ? optics.overlay.absorption : turbidSigma(optics.overlay.absorption, k);
      o.uAbsorption.value.set(ab[0], ab[1], ab[2]);
      o.setWaterline(split);
      o.update(this.time, depth);
    }
    if (this.medium) {
      this.medium.visible = true;
      this.medium.setWaterline(split);
      const sig = optics === OPTICS_BY_BODY.sea ? optics.extinction : turbidSigma(optics.extinction, k);
      this.medium.uSigma.value.set(sig[0], sig[1], sig[2]);
      // The medium glows with the sun reaching the camera's depth (waterLook's oracle in lib/water_look) — the sea's
      // rule unchanged, a lake's or a river's through its own albedo and diffuse Kd.
      const sd = (uSunDir.value as THREE.Vector3).y;
      if (optics !== OPTICS_BY_BODY.sea) underwaterInscatterBody(optics, this._light.illum, depth, this._inscatter, k);
      else if (this._light.physical) underwaterInscatterLit(this._light.illum, depth, this._inscatter);
      else underwaterInscatter(sd, this._light.illum, depth, this._inscatter);
      this.medium.uInscatter.value.copy(this._inscatter);
      this.lookStats.inscatter = [this._inscatter.x, this._inscatter.y, this._inscatter.z].map((v) => Math.round(v * 1e4) / 1e4);
    }
    this.lookStats.mediumVisible = this.medium?.visible ?? false;
    if (this.shafts) {
      // Shafts are world sheets around the eye: only with the whole lens under (in a split they would stand in the air).
      const m = this.shafts.mesh;
      if (state === 'below') {
        // The fan rides the camera (scene space); its streaks lean with the sun.
        m.position.set(this.camPos.x, this.camPos.y + 8, this.camPos.z); // camPos is already ABSOLUTE (the rebase note above)
        const sd = uSunDir.value as THREE.Vector3;
        m.rotation.y = Math.atan2(sd.x, sd.z);
        this.shafts.uniforms.slant.value.set(Math.hypot(sd.x, sd.z) / Math.max(0.05, sd.y) * 0.25, 0);
        this.shafts.uniforms.depth.value = Math.max(0, depth);
        // No beams at night; by day the key's own curve (≈1 at the tuned noon — session 3); a turbid body scatters them out.
        const keyK = this._light.physical ? lum709(this._light.sun.r, this._light.sun.g, this._light.sun.b) : 1;
        this.shafts.uniforms.intensity.value = Math.max(0, sd.y) * 1.2 * keyK * optics.shaftGain;
        m.visible = true;
        stage4Stats.shaftsVisible = true;
      } else {
        m.visible = false;
        stage4Stats.shaftsVisible = false;
      }
    }
  }

  /** The DRAWN sea surface over absolute (x, z): the vertex wave set × the shoal + the interaction ripple (the mesh's own rule). */
  private drawnSeaAt(x: number, z: number): number {
    const sw = this.seaWaves;
    if (sw) {
      // Shader gaps R5: the provider's DRAWN surface (Gerstner: the vertex set × the shoal, exactly the lines below; FFT:
      // the displaced triangle of the sea mesh over the point, each vertex shoaled and faded as the vertex stage does).
      sw.setRenderShift(this.scene.position.x, this.scene.position.z);
      return this.seaLevel + sw.sampleDrawnHeight(x, z, this.time, this.mod, this.seaScaleAt ?? ((px, pz) => this.seaShoalAt(px, pz))) + (waterFx().field?.heightAtWorld(x, z) ?? 0);
    }
    const rx = x + this.scene.position.x, rz = z + this.scene.position.z;
    const shoal = this.seaShoalAt(x, z);
    return this.seaLevel + sampleHeight(this.vertexWaves, rx, rz, this.time, this.mod) * shoal + (waterFx().field?.heightAtWorld(x, z) ?? 0);
  }

  /** `__fftOcean(x?, z?)`: the sea's wave provider, why, the patch's state; at a point the drawn / live surfaces and the bound. */
  private fftOceanState(x?: number, z?: number): object {
    const sw = this.seaWaves;
    const fft = sw instanceof FftWaveProvider ? sw : null;
    const out: Record<string, unknown> = {
      kind: sw?.kind ?? null, reason: this.fftReason, error: this.fftError || undefined,
      wanted: activePreset().fftOcean === true, killSwitch: fftOceanKilled(), webgpu: !!this.app?.renderer.isWebGPU,
      queryLatencyS: sw?.queryLatencyS ?? null, heightErrorBoundM: sw ? Math.round(sw.heightErrorBoundM() * 1e5) / 1e5 : null,
      seaAmp: Math.round(this.mod.ampScale * 1000) / 1000, time: Math.round(this.time * 100) / 100,
    };
    if (fft) {
      out.fade = fft.fade;
      out.patch = { N: fft.grid.N, P: fft.grid.P, texel: fft.grid.texel, minWavelength: fft.grid.minWavelength, vertexWavelength: fft.grid.vertexWavelength, chop: fft.grid.chop, ...(fft.patch?.stats ?? {}), dispatchesPerFrame: fft.patch?.dispatchesPerFrame ?? 0 };
      out.band = { ...fft.bandVariance, hsBand: Math.round(4 * Math.sqrt(fft.bandVariance.normal) * 100) / 100, slopeVar: Math.round(fft.totalSlopeVariance() * 1e5) / 1e5, maxAmplitude: Math.round(fft.maxAmplitude() * 1000) / 1000 };
      out.uniforms = { amp: fft.uAmp.value, ampPrev: fft.uAmpPrev.value, lift: fft.uLift.value, liftPrev: fft.uLiftPrev.value, varLow: fft.uVarLow.value, varShort: fft.uVarShort.value, shift: [(fft.uPatchShift.value as THREE.Vector2).x, (fft.uPatchShift.value as THREE.Vector2).y] };
      out.query = { ...fft.queryModes, drawnVertexSource: fft.drawnVertexSource, drawnGridAligned: fft.drawnGridAligned };
      out.stats = { ...fft.stats };
    }
    if (sw && Number.isFinite(x) && Number.isFinite(z)) {
      const px = x as number, pz = z as number;
      sw.setRenderShift(this.scene.position.x, this.scene.position.z);
      out.at = {
        x: px, z: pz,
        drawn: Math.round(this.drawnSeaAt(px, pz) * 1e4) / 1e4,
        live: waterFx().provider?.liveAt(px, pz) ?? null,
        continuous: Math.round((this.seaLevel + sw.sampleDisplacement(px, pz, this.time, this.mod).dy) * 1e4) / 1e4,
      };
    }
    return out;
  }

  /** The vertex stage's shoal over an absolute point (1 without a shoreline field) — ShorelineField's smoothstep. */
  private seaShoalAt(x: number, z: number): number {
    if (!this.shoreline) return 1;
    const d = this.shoreline.depthAt(x - this.centerX, z - this.centerZ);
    const t = Math.min(1, Math.max(0, d / this.shoreline.shoalDepth));
    return t * t * (3 - 2 * t);
  }

  /**
   * Shader gaps R5: the sea's waves — the FFT patch when the tier asks for it on WebGPU (lib/fft_ocean), else the Gerstner
   * set behind the same interface. A patch that fails to build falls back to Gerstner and says why (`__fftOcean()`).
   */
  private buildSeaWaves(waves: GerstnerWave[]): WaveProvider {
    this.seaWaves?.dispose();
    this.fftError = '';
    const xr = !!(this.app?.renderer.native as unknown as { xr?: { isPresenting?: boolean } } | undefined)?.xr?.isPresenting;
    const d = fftOceanDecision(activePreset(), { webgpu: !!this.app?.renderer.isWebGPU, xrPresenting: xr, killSwitch: fftOceanKilled() });
    this.fftReason = d.reason;
    if (d.on && this.app) {
      try {
        if (!this.weather) this.weather = (this.app.getModule?.('Weather') as WeatherModule | undefined) ?? null;
        const p0 = waves[0];
        const heading = p0 ? Math.atan2(p0.dirZ, p0.dirX) + this.mod.dirRot : 0;
        const w = this.weather?.wind() ?? { strength: 0.45, kind: 'none', density: 0 };
        const fft = new FftWaveProvider(this.app.renderer.native as never, {
          ...FFT_OCEAN,
          vertexWavelength: VERTEX_WAVE_CUTOFF,
          loopPeriodS: WATER_CLOCK_WRAP_S, // the water clock's wrap (tick): the patch repeats exactly then — no pop
          spectrum: fftSpectrumFor(w.strength, w.kind === 'rain' ? w.density : 0, heading),
        });
        this.seaScaleAt = this.seaVertexScale;
        return fft;
      } catch (e) {
        this.fftReason = 'error';
        this.fftError = String(e).slice(0, 200);
        console.warn('[AtlasWater] FFT ocean failed to build — the Gerstner sea draws', e);
      }
    }
    // THE RIDER LAW below ultra (2026-09-30): the Gerstner sea's riders, swimmers and the waterline stand on the DRAWN
    // triangles too (the drawn grid is set every frame in stepSeaWaves) — the continuous query sat 0.2–0.4 m off the
    // 8 m mesh's chords and the full set added 0.66 m of short waves the mesh never draws.
    this.seaScaleAt = this.seaVertexScale;
    return new GerstnerWaveProvider(waves, { vertexWavelengthCutoff: VERTEX_WAVE_CUTOFF, vertexSpacing: FOOTPRINT / SEGMENTS, liveOnDrawn: true });
  }

  /** Each vertex of the sea mesh is shoaled and faded toward the rim (the vertex stage's own rule) — the drawn query's scale. */
  private readonly seaVertexScale = (x: number, z: number): number => {
    const r = Math.hypot(x - this.centerX, z - this.centerZ);
    const tf = Math.min(1, Math.max(0, (r - NEAR_SEA_FADE.start) / (NEAR_SEA_FADE.end - NEAR_SEA_FADE.start)));
    return this.seaShoalAt(x, z) * (1 - tf * tf * (3 - 2 * tf));
  };

  /**
   * Shader gaps R5, per frame (after the sea state is slewed): the FFT patch's spectrum follows the wind (cross-faded in
   * the provider), its world anchor follows the rebase, its drawn grid follows the recentre, and its compute runs.
   */
  private stepSeaWaves(dt: number): void {
    // The drawn grid follows the recentre for BOTH seas (THE RIDER LAW: every height query solves the mesh's triangles).
    if (this.seaWaves && this.mesh) {
      this.seaWaves.setDrawnGrid({ originX: this.centerX - FOOTPRINT / 2, originZ: this.centerZ - FOOTPRINT / 2, spacing: FOOTPRINT / SEGMENTS, cells: SEGMENTS });
    }
    const fft = this.seaWaves instanceof FftWaveProvider ? this.seaWaves : null;
    if (!fft || !this.mesh) return;
    const w = this.weather?.wind() ?? { strength: 0.45, kind: 'none', density: 0 };
    const fluid = waterFx().enabled;
    const p0 = this.waves[0];
    const heading = p0 ? Math.atan2(p0.dirZ, p0.dirX) + this.mod.dirRot : 0;
    fft.setSpectrum(fftSpectrumFor(fluid ? w.strength : 0.45, fluid && w.kind === 'rain' ? w.density : 0, heading));
    fft.setRenderShift(this.scene.position.x, this.scene.position.z);
    if (this.mesh.visible) fft.update(this.time, dt, this.mod);
    // The projector's grid is world-anchored like the material's (the FFT patch is): its offset is the mesh's render position.
    this.caustics?.uWorldOffset.value.set(this.centerX + this.scene.position.x, this.centerZ + this.scene.position.z);
  }

  /** Hide the underwater view (the lens is all air): the fog back to the air's, the veil / medium / shafts off. */
  private hideUnderwaterView(): void {
    if (this.fogKey !== '') { setUnderwaterFog(null); this.fogKey = ''; }
    if (this.overlay) { this.overlay.mesh.visible = false; this.overlay.uIntensity.value = 1; this.overlay.setWaterline(null); }
    if (this.medium) { this.medium.visible = false; this.medium.setWaterline(null); }
    if (this.shafts) { this.shafts.mesh.visible = false; stage4Stats.shaftsVisible = false; }
  }

  // ─── Water realism wave (2026-09-25) ──────────────────────────────────────

  /** The cloud field's weather map (R = coverage) — what the reflection reads (R1 follow-up). */
  private cloudWeatherMap(): THREE.Texture | null {
    const clouds = this.app?.getModule?.('Clouds') as { weatherMap?: THREE.Texture | null } | undefined;
    return clouds?.weatherMap ?? null;
  }

  /** A graphics change that moves the water's look rebuilds the bodies (their graphs are per configuration). */
  private onQualityChanged(): void {
    if (!this.mesh || !this.seed) return;
    const sig = waterLookSignature(activePreset());
    // Shader gaps R5: the FFT decision is part of the sea's graph (a different wave provider) — a change rebuilds.
    const xr = !!(this.app?.renderer.native as unknown as { xr?: { isPresenting?: boolean } } | undefined)?.xr?.isPresenting;
    const fftD = fftOceanDecision(activePreset(), { webgpu: !!this.app?.renderer.isWebGPU, xrPresenting: xr, killSwitch: fftOceanKilled() });
    const fftNow = fftD.on;
    if (fftNow !== (this.seaWaves?.kind === 'fft') && !(fftNow && this.fftReason === 'error')) { this.build(this.seed); return; }
    // The sea did not change but its reason may have (the flag off after the kill switch): `__fftOcean()` reads it.
    if (!fftNow && this.fftReason !== 'error') this.fftReason = fftD.reason;
    if (sig === this.lookSig) {
      // Live-only switches (the budget, the medium, the life, the crowns) re-read without a rebuild.
      this.look = waterLookFor(activePreset());
      return;
    }
    this.build(this.seed);
  }

  /**
   * Per frame: bind the sky PMREM and the cloud map as they appear, drive the
   * cloud drift, the far sea's uniforms (it mirrors the near sea), the planar
   * reflection budget and the splash crowns.
   */
  private tickLook(dt: number, illum: number): void {
    const h = this.handles;
    if (!h) return;
    const look = this.look;
    const bodies = [h, this.lakeHandles];
    const far = currentFarSea();
    // R1: scene.environment is the sky bake's PMREM (identity-stable after its first bake; null on a flat sky).
    if (look.sky) {
      const env = (this.scene.environment as THREE.Texture | null) ?? null;
      if (env !== this.boundEnv) {
        for (const b of bodies) b?.setEnvMap(env);
        far?.setEnvMap(env);
        this.boundEnv = env;
        this.lookStats.envBound = !!env;
      }
      // The cube holds the scale it was baked at; the sky carries the exposure curve between bakes in
      // environmentIntensity (SkyModule.drivePhysicalSky) — the water reads the cube directly, so it takes
      // the same factor or its reflection jumps at every re-bake while the lit world stays continuous.
      const ei = (this.scene as unknown as { environmentIntensity?: number }).environmentIntensity ?? 1;
      for (const b of bodies) if (b?.uEnvIntensity) b.uEnvIntensity.value = ei;
      if (far?.uEnvIntensity) far.uEnvIntensity.value = ei;
      this.lookStats.envIntensity = ei;
    }
    // R1 follow-up: the cloud coverage map + its drift (the cloud-shadow drive's addressing).
    if (look.clouds) {
      const map = this.cloudWeatherMap();
      if (map && map !== this.boundCloudMap) {
        for (const b of bodies) b?.setCloudTexture(map);
        far?.setCloudTexture(map);
        this.boundCloudMap = map;
      }
      this.lookStats.cloudBound = !!this.boundCloudMap;
      const clouds = this.app?.getModule?.('Clouds') as { renderer?: { windOffset?: { x: number; y: number } } } | undefined;
      const wind = clouds?.renderer?.windOffset ?? null;
      const sp = this.scene.position;
      const ox = (wind?.x ?? 0) - sp.x, oz = (wind?.y ?? 0) - sp.z;
      const cov = (this.weather as unknown as { getCloudCoverage?: () => number } | null)?.getCloudCoverage?.() ?? 0.4;
      const strength = Math.min(1, 0.45 + cov * 0.7);
      for (const b of [h, this.lakeHandles, far]) { if (!b) continue; b.uCloudOffset.value.set(ox, oz); b.uCloudStrength.value = strength; }
    }
    // R5: the far sea mirrors the near sea's clock, sun, sea state and drift; it steps
    // aside under the near sea's footprint (render space) while the near sea shows.
    if (far) {
      far.uTime.value = h.uTime.value;
      far.uSunDirection.value.copy(h.uSunDirection.value);
      far.uIlluminance.value = illum;
      far.uSunLight.value.copy(h.uSunLight.value);
      far.uSkyColor.value.copy(h.uSkyColor.value);
      far.uWaveAmpScale.value = h.uWaveAmpScale.value;
      far.uWaveDirRot.value = h.uWaveDirRot.value;
      far.uDetailDrift.value.copy(h.uDetailDrift.value);
      const sp = this.scene.position;
      far.uNearSeaRect.value.set(this.centerX + sp.x, this.centerZ + sp.z, NEAR_SEA_HALF_M - FAR_SEA_CARVE_MARGIN_M, this.mesh?.visible ? 1 : 0);
    }
    // R15: ONE planar mirror renders per frame — the body whose water is nearest the camera.
    if (look.budget && ++this.planarTick % 10 === 0) this.stepPlanarBudget();
    else if (!look.budget && this.planarBody !== null) {
      for (const b of [h, this.lakeHandles]) { b?.setPlanarActive(true); if (b) b.uPlanarWeight.value = 1; }
      waterPlanarBodies().river?.setActive(true);
      this.planarBody = null;
    }
    // R7: the ground under the sea reads the sea level, the sun and the clock.
    setBedWater(this.seaLevel, uSunDir.value as THREE.Vector3, illum, this.time, this.mesh?.visible ?? false);
    // Shader gaps R1: the ground under the LAKES and RIVERS reads the body table (the tier's bed caustics, like the sea).
    this.stepBodyTable();
    this.crowns?.update(dt, this.scene.position);
  }

  /**
   * Shader gaps R1: (re)pack the body table the ground shades lakes and rivers from — on a new lake region, after the
   * camera moves 96 m, or every 3 s (the river network may land late); the render-space origin follows every frame.
   */
  private stepBodyTable(): void {
    const on = this.look.bedCaustics && waterBodiesEnabled() && this.seed !== '';
    if (on) {
      const now = performance.now();
      const p = this.bodyPackAt;
      const lakesShown = this.lakeMesh?.visible ?? false;
      const region = lakesShown ? this.lakeRegion : null;
      const due = !p || p.region !== region || Math.hypot(this.camPos.x - p.x, this.camPos.z - p.z) > 96 || now - p.ms > 3000;
      if (due && (!p || now - p.ms > 250)) {
        const labels = region && this.bodyLabels?.region === region ? this.bodyLabels : null;
        const rivers = riverSegmentsNear(this.seed, this.camPos.x, this.camPos.z, RIVER_RADIUS_M);
        this.bodyTable = packBodyTable(labels, rivers, this.camPos.x, this.camPos.z);
        setBedBodies(this.bodyTable);
        this.bodyPackAt = { x: this.camPos.x, z: this.camPos.z, region, ms: now };
      }
    }
    setBedBodiesFrame(this.scene.position, on && this.bodyTable !== null);
    setSeaBedFix(seaBedFixEnabled()); // the R7 sea bed's corrected mix — the default; `atlas:sea-bed-fix = 'off'` = the old look (THE MIX LAW)
  }

  /** Shader gaps R1: the `__waterBody(x?, z?)` lever — the body at the camera or a point, the packed table, the waterline. */
  private waterBodyState(x?: number, z?: number): object {
    const px = x ?? this.camPos.x, pz = z ?? this.camPos.z;
    const b = bodyAt(px, pz);
    const r = (v: number, k = 1000): number => Math.round(v * k) / k;
    const t = this.bodyTable;
    return {
      enabled: waterBodiesEnabled(),
      at: [r(px, 10), r(pz, 10)],
      body: b ? {
        kind: b.kind, id: b.id, level: r(b.level), surface: r(b.surfaceAt(px, pz)), turbidity: r(b.turbidity),
        flow: { x: r(b.flow.x), z: r(b.flow.z), speed: r(b.flow.speed) },
        optics: { kd: b.optics.kd.map((v) => r(v)), extinction: b.optics.extinction.map((v) => r(v)), albedo: b.optics.albedo.map((v) => r(v)), secchiM: r(b.optics.secchiM, 10), visibilityM: r(b.optics.visibilityM, 10) },
      } : null,
      camera: {
        state: this.wlState, body: this.wlStats.body, surfaceY: this.wlStats.lastSurfaceY, camY: r(this.camPos.y), originUnder: this.submerged,
        line: this.wlStats.lastLine, corners: this.wl ? this.wl.corners.map((v) => r(v, 1e4)) : null,
        xrWhole: this.wlStats.xrWhole, splitFrames: this.wlStats.splitFrames, belowFrames: this.wlStats.belowFrames,
        overlay: this.overlay ? { visible: this.overlay.mesh.visible, mode: this.overlay.uWaterline.value.w, deep: this.overlay.uDeepColor.value.getHexString() } : null,
        medium: this.medium ? { visible: this.medium.visible, mode: this.medium.uWaterline.value.w, sigma: this.medium.uSigma.value.toArray().map((v) => r(v)) } : null,
        fog: this.fogKey,
      },
      table: t ? {
        lakes: t.lakeCount, rivers: t.riverCount, lakeCells: t.lakeCells, riverCells: t.riverCells, origin: [t.originX, t.originZ],
        packedAt: [r(t.atX, 10), r(t.atZ, 10)], slots: t.slots.map((sl, i) => (sl ? { slot: i, id: sl.id, level: r(sl.level), turbidity: r(sl.turbidity) } : null)).filter(Boolean),
      } : null,
      bed: bedShadingStats(),
      /** Probe: paint what the ground's bodies branch decoded (R = depth / 8, G = lake id / 63, B = turbidity / 2). */
      debugBed: (on: boolean) => { setBedBodiesDebug(on); return on; },
      /** Probe A/B: false = the states are computed but the veil and the medium are not drawn. */
      veil: (on: boolean) => { this.probeVeilOff = !on; return on; },
    };
  }

  /** R15: the planar budget — the nearest visible body keeps its mirror; the others fall back to the sky PMREM. */
  private stepPlanarBudget(): void {
    const h = this.handles;
    if (!h) return;
    const cands: PlanarCandidate[] = [];
    const cx = this.camPos.x, cz = this.camPos.z;
    // Sea: over (or beside) baked sea — distance to the nearest wet texel is the shore distance; 0 when over it.
    const seaD = this.mesh?.visible && this.shoreline ? (this.shoreline.depthAt(cx - this.centerX, cz - this.centerZ) > 0.05 ? 0 : Math.max(0, Math.min(Math.abs(cx - this.centerX), Math.abs(cz - this.centerZ)) - NEAR_SEA_HALF_M * 0.5)) : Infinity;
    cands.push({ name: 'sea', distance: seaD, visible: !!this.mesh?.visible && !!h.reflectorNode });
    let lakeD = Infinity;
    if (this.lakeRegion && this.lakeMesh?.visible) {
      const lv = nearestLakeLevel(this.lakeRegion, cx, cz);
      lakeD = lakeSurfaceAtBaked(cx, cz) !== null ? 0 : lv !== null ? 60 : Infinity;
    }
    cands.push({ name: 'lake', distance: lakeD, visible: !!this.lakeMesh?.visible && !!this.lakeHandles?.reflectorNode });
    const river = waterPlanarBodies().river;
    if (river) cands.push({ name: 'river', distance: river.distance(), visible: river.visible() });
    const pick = pickPlanarBody(cands, this.planarBody);
    this.planarBody = pick;
    this.lookStats.planar = pick;
    const set = (hh: RealisticWaterHandles | null, on: boolean): void => { if (!hh) return; hh.setPlanarActive(on); hh.uPlanarWeight.value = on ? 1 : 0; };
    set(h, pick === 'sea');
    set(this.lakeHandles, pick === 'lake');
    river?.setActive(pick === 'river');
  }

  /**
   * Probe: render one frame (camera stack + post, the `__framePixels` path) and
   * return per-rect channel means (0..255; rects in screen fractions x0,y0,x1,y1
   * from the TOP-left) and, for a column band [x0,x1], the mean luminance of
   * every sampled row (the seam profile).
   */
  private async frameRegions(rects: Array<[number, number, number, number]>, band?: [number, number]): Promise<object> {
    const app = this.app;
    if (!app) return { error: 'no app' };
    const native = app.renderer.native;
    if ((app.renderer as unknown as { paused?: boolean }).paused) return { paused: true };
    const size = native.getDrawingBufferSize(new THREE.Vector2());
    const w = Math.max(1, Math.round(size.x)), h = Math.max(1, Math.round(size.y));
    const rt = new THREE.RenderTarget(w, h);
    const prev = native.getRenderTarget();
    try {
      native.setRenderTarget(rt);
      app.renderer.renderFrame(app.world.scene);
      native.setRenderTarget(prev);
      // THE READBACK LAW: a w·4-byte row that is not a multiple of 256 comes back padded on WebGPU — tighten first.
      const px = tightenReadback(await native.readRenderTargetPixelsAsync(rt, 0, 0, w, h) as ArrayLike<number> & { constructor: { name: string } }, w, h);
      const scale = px instanceof Uint8Array || px instanceof Uint8ClampedArray ? 1 : 255;
      const flip = !(native as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend?.isWebGPUBackend;
      const at = (x: number, yTop: number): number => ((flip ? h - 1 - yTop : yTop) * w + x) * 4;
      const out = rects.map(([x0, y0, x1, y1]) => {
        let r = 0, g = 0, b = 0, n = 0;
        const X0 = Math.floor(x0 * w), X1 = Math.max(X0 + 1, Math.floor(x1 * w)), Y0 = Math.floor(y0 * h), Y1 = Math.max(Y0 + 1, Math.floor(y1 * h));
        for (let y = Y0; y < Y1; y += 2) for (let x = X0; x < X1; x += 2) { const i = at(x, y); r += px[i] * scale; g += px[i + 1] * scale; b += px[i + 2] * scale; n++; }
        return { r: r / n, g: g / n, b: b / n, lum: (0.299 * r + 0.587 * g + 0.114 * b) / n };
      });
      let rows: number[] | null = null;
      if (band) {
        rows = [];
        const X0 = Math.floor(band[0] * w), X1 = Math.max(X0 + 1, Math.floor(band[1] * w));
        for (let y = 0; y < h; y += 2) { let s = 0, n = 0; for (let x = X0; x < X1; x += 2) { const i = at(x, y); s += (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) * scale; n++; } rows.push(Math.round((s / n) * 10) / 10); }
      }
      return { w, h, rects: out, rows };
    } finally { native.setRenderTarget(prev); rt.dispose(); }
  }

  /** Probe: where the near sea's rim crosses the screen along the camera's heading (screen v from the top, 0..1), or null. */
  private seamScreen(): object | null {
    const cam = this.app?.renderer.cameras.main as THREE.PerspectiveCamera | undefined;
    if (!cam || !this.mesh?.visible) return null;
    const fwd = new THREE.Vector3(); cam.getWorldDirection(fwd);
    const fx = fwd.x, fz = fwd.z, fl = Math.hypot(fx, fz) || 1;
    const dx = fx / fl, dz = fz / fl;
    // Exit distance of the ray (camPos, d) from the near sea's square.
    const half = NEAR_SEA_HALF_M;
    const tx = dx > 0 ? (this.centerX + half - this.camPos.x) / dx : dx < 0 ? (this.centerX - half - this.camPos.x) / dx : Infinity;
    const tz = dz > 0 ? (this.centerZ + half - this.camPos.z) / dz : dz < 0 ? (this.centerZ - half - this.camPos.z) / dz : Infinity;
    const t = Math.min(tx, tz);
    if (!Number.isFinite(t) || t <= 0) return null;
    const sp = this.scene.position;
    const p = new THREE.Vector3(this.camPos.x + dx * t + sp.x, this.seaLevel + sp.y, this.camPos.z + dz * t + sp.z).project(cam);
    return { distance: Math.round(t), v: (1 - p.y) / 2, u: (p.x + 1) / 2 };
  }

  /** Probe: the heading (yaw, three's rotation.y convention) from the camera toward the deepest baked water. */
  private seawardYaw(): number | null {
    // THE OPEN WATER NEAR YOU (2026-09-25, session 2): the heading whose ray stays over the sea from the
    // camera out, nearer water weighing more — never the deepest texel of the 3 km bake, which lies beyond
    // a headland as often as not (the look probe shot a hillside as "seaward", the sea a sliver on the left).
    const n = SHORE_RES;
    const depthAt = (x: number, z: number): number => {
      const u = (x - this.centerX) / FOOTPRINT + 0.5, v = (z - this.centerZ) / FOOTPRINT + 0.5;
      if (u < 0 || u > 1 || v < 0 || v > 1) return -1;
      return this.seaLevel - this.scratch[Math.round(v * (n - 1)) * n + Math.round(u * (n - 1))];
    };
    let best = 0, bestYaw: number | null = null;
    for (let i = 0; i < 48; i++) {
      const yaw = (i / 48) * Math.PI * 2;
      const fx = -Math.sin(yaw), fz = -Math.cos(yaw); // three's rotation.y: forward is (−sin, −cos)
      let score = 0;
      for (let d = 15; d <= 600; d += 15) if (depthAt(this.camPos.x + fx * d, this.camPos.z + fz * d) > 0.3) score += 1 / (1 + d / 150);
      if (score > best) { best = score; bestYaw = yaw; }
    }
    return bestYaw;
  }

  /** `__waterLook()` — the look and its bindings (probes). */
  private lookState(): object {
    const maps = waterNormalMaps(this.look.spectrum);
    return {
      look: this.look, sig: this.lookSig, ...this.lookStats,
      mapStats: maps.stats,
      bed: { tex: !!this.bedTex, ...bedShadingStats() },
      planarActive: { sea: this.handles?.planarActive ?? false, lake: this.lakeHandles?.planarActive ?? false, river: waterPlanarBodies().river?.visible() ? (waterPlanarBodies().river?.active() ?? false) : false },
      far: !!currentFarSea(),
      life: this.life?.stats() ?? null,
      crowns: this.crowns?.stats() ?? null,
      medium: this.medium ? { visible: this.medium.visible, sigma: this.medium.uSigma.value.toArray(), inscatter: this.medium.uInscatter.value.toArray() } : null,
      envWeight: this.handles?.uEnvWeight.value ?? 0,
      // Session 3: the water's light this frame (lib/water_lighting — the ground's photometric numbers).
      light: { illum: Math.round(this._light.illum * 1e4) / 1e4, sun: [this._light.sun.r, this._light.sun.g, this._light.sun.b].map((v) => Math.round(v * 1e4) / 1e4), physical: this._light.physical },
    };
  }

  // ─── Interactive water ────────────────────────────────────────────────────

  /** The surface every stamper asks: still level, live height, live slope — ABSOLUTE coords. */
  private surfaceProvider(): WaterSurfaceProvider {
    const seaStill = (x: number, z: number): number | null => {
      if (!this.mesh || !this.mesh.visible || !this.shoreline) return null;
      const lx = x - this.centerX, lz = z - this.centerZ;
      if (Math.abs(lx) > FOOTPRINT * 0.5 || Math.abs(lz) > FOOTPRINT * 0.5) return null;
      return this.shoreline.depthAt(lx, lz) > 0.05 ? this.seaLevel : null;
    };
    // Rivers are COMMONS rivers: off-world the channel field still answered
    // for any coordinate under a Commons reach — wading on dry ground in a
    // personal world whose plot happened to overlap one (2026-09-12).
    const lakeStill = (x: number, z: number): number | null =>
      ((this.lakeMesh?.visible ?? false) ? lakeSurfaceAtBaked(x, z) : null)
      ?? (this.seed && atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId ? riverWaterAt(this.seed, x, z) : null);
    // The shaders evaluate the wave phase at RENDER-space coordinates
    // (uWorldOffset carries the rebase) — the CPU query must too.
    const rx = (x: number): number => x + this.scene.position.x;
    const rz = (z: number): number => z + this.scene.position.z;
    const seaShoal = (x: number, z: number): number => {
      if (!this.shoreline) return 1;
      const d = this.shoreline.depthAt(x - this.centerX, z - this.centerZ);
      const t = Math.min(1, Math.max(0, d / this.shoreline.shoalDepth));
      return t * t * (3 - 2 * t);
    };
    const onCommons = (): boolean => this.seed !== '' && atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId;
    return {
      // The order is lakeStill's own: a baked lake first, then a river channel, then the sea.
      kindAt: (x, z) => ((this.lakeMesh?.visible ?? false) && lakeSurfaceAtBaked(x, z) !== null ? 'lake'
        : onCommons() && riverWaterAt(this.seed, x, z) !== null ? 'river'
          : seaStill(x, z) !== null ? 'sea' : null),
      seaAmp: () => this.mod.ampScale,
      stillAt: (x, z) => lakeStill(x, z) ?? seaStill(x, z),
      liveAt: (x, z) => {
        const lake = lakeStill(x, z);
        const ripple = waterFx().field?.heightAtWorld(x, z) ?? 0;
        if (lake !== null) return lake + sampleHeight(this.lakeWaves, rx(x), rz(z), this.time, this.lakeMod) + ripple;
        const sea = seaStill(x, z);
        if (sea === null) return null;
        const sw = this.seaWaves;
        if (sw) { sw.setRenderShift(this.scene.position.x, this.scene.position.z); return sea + sw.sampleLiveHeight(x, z, this.time, this.mod, this.seaScaleAt ?? seaShoal) + ripple; } // shader gaps R5
        return sea + sampleHeight(this.waves, rx(x), rz(z), this.time, this.mod) * seaShoal(x, z) + ripple;
      },
      slopeAt: (x, z) => {
        const lake = lakeStill(x, z);
        const r = waterFx().field?.slopeAtWorld(x, z) ?? { dx: 0, dz: 0 };
        const sw = this.seaWaves;
        if (sw && lake === null) sw.setRenderShift(this.scene.position.x, this.scene.position.z); // shader gaps R5
        const n = lake !== null
          ? sampleNormal(this.lakeWaves, rx(x), rz(z), this.time, this.lakeMod)
          : seaStill(x, z) !== null ? (sw ? sw.sampleNormal(x, z, this.time, this.mod) : sampleNormal(this.waves, rx(x), rz(z), this.time, this.mod)) : { nx: 0, ny: 1, nz: 0 };
        const k = lake !== null ? 1 : seaShoal(x, z);
        return { dx: (-n.nx / Math.max(0.2, n.ny)) * k + r.dx, dz: (-n.nz / Math.max(0.2, n.ny)) * k + r.dz };
      },
    };
  }

  /**
   * Per frame: the wind's sea state (slewed, never a jump) into the wave
   * uniforms, the foam threshold and the detail drift; the interaction field
   * follows the camera and steps; rain stamps drops where there is water.
   */
  /**
   * R10: one foam step per frame (RenderSub/36, its own target — the CausticsProjector pattern),
   * centred on the ABSOLUTE camera with the rebase shift folded in (the material samples render
   * space), drifting downwind, thresholded at the sea state's own whitecap Jacobian; the material
   * is handed the newest target and the same centre.
   */
  private stepFoamField(dt: number): void {
    const f = this.foamField, h = this.handles;
    if (!f || !h || !this.mesh?.visible || !this.app) return;
    const fx = waterFx();
    if (fx.enabled) this._foamDrift.set(this._windDrift.x, this._windDrift.y).normalize().multiplyScalar(0.35 + 0.45 * this.seaState.driftScale);
    else this._foamDrift.set(0.4, 0.3);
    (f.uDrift as unknown as { value: THREE.Vector2 }).value.copy(this._foamDrift);
    (f.uFoamJ as unknown as { value: number }).value = h.uFoamJacobian.value;
    (f.uInteractionCenter as unknown as { value: THREE.Vector2 }).value.copy(h.uInteractionCenter.value);
    try {
      f.step(this.app.renderer.native as never, dt, this.time, this.camPos.x, this.camPos.z, { x: this.scene.position.x, z: this.scene.position.z });
    } catch { this.foamField = null; return; }
    h.setFoamFieldTexture(f.texture);
    h.uFoamCenter.value.copy((f.uCenter as unknown as { value: THREE.Vector2 }).value);
  }

  private tickFluid(dt: number): void {
    if (!this.handles) return;
    const fx = waterFx();
    fx.enabled = activePreset().fluidPhysics !== false;
    // W7 (2026-10-05): THE TIER RULING — rings follow the fluid flag (`enabled`), particles the particle budget (never
    // on minimal), sounds every tier. A density raised later builds the emitters here (they were built only at a
    // rebuild, so a player who turned particles on mid-session never got a splash).
    this.fxTier = waterFxTier(activeTier(), fx.enabled, activePreset().particleCap ?? 0);
    fx.particles = this.fxTier.particles;
    fx.particleScale = this.fxTier.particleScale;
    if (fx.particles && (!this.splashEmitter || !this.dripEmitter)) {
      // Once per tier / density step: a construction that throws must not be retried every frame.
      const sig = `${activeTier()}|${activePreset().particleCap ?? 0}`;
      if (sig !== this.emitterTry) {
        this.emitterTry = sig;
        if (!this.splashEmitter) this.buildSplashEmitter();
        if (!this.dripEmitter) this.buildDripEmitter();
      }
    }
    if (!this.weather) this.weather = (this.app?.getModule?.('Weather') as WeatherModule | undefined) ?? null;
    const w = this.weather?.wind() ?? { x: 1, z: 0, strength: 0.45, kind: 'none', density: 0 };
    const storm = w.kind === 'rain' ? w.density : 0;
    // ── Sea state from the wind (only with fluid physics on; off = the authored sea exactly).
    const target = fx.enabled ? seaStateFor(w.strength, storm) : seaStateFor(0.45);
    const d = Math.min(0.1, Math.max(0, dt));
    this.mod.ampScale = slew(this.mod.ampScale, target.ampScale, AMP_SLEW_PER_S, d);
    const primary = this.waves[0];
    const rotTarget = fx.enabled && primary ? windHeadingRotation(primary.dirX, primary.dirZ, w.x, w.z) : 0;
    this.mod.dirRot = slewAngle(this.mod.dirRot, rotTarget, HEADING_SLEW_RAD_PER_S, d);
    this.seaState = target;
    this.lakeMod.ampScale = 1 + (this.mod.ampScale - 1) * 0.5;
    this.lakeMod.dirRot = this.mod.dirRot;
    const foamJ = slew(this.handles.uFoamJacobian.value, target.foamJacobian, 0.1, d);
    this.handles.uWaveAmpScale.value = this.mod.ampScale;
    this.handles.uWaveDirRot.value = this.mod.dirRot;
    this.handles.uFoamJacobian.value = foamJ;
    // Detail ripples drift downwind at the authored magnitude (|(1, 0.8)| = 1.28) × the sea state.
    const driftMag = 1.28 * target.driftScale;
    if (fx.enabled) this._windDrift.set(w.x, w.z).normalize().multiplyScalar(driftMag);
    else this._windDrift.set(1, 0.8);
    this.handles.uDetailDrift.value.lerp(this._windDrift, Math.min(1, d * 0.5));
    const rain = fx.enabled && w.kind === 'rain' ? w.density : 0;
    this.handles.uRainIntensity.value += (rain - this.handles.uRainIntensity.value) * Math.min(1, d * 2);
    if (this.lakeHandles) {
      this.lakeHandles.uWaveAmpScale.value = this.lakeMod.ampScale;
      this.lakeHandles.uWaveDirRot.value = this.lakeMod.dirRot;
      this.lakeHandles.uFoamJacobian.value = foamJ;
      this.lakeHandles.uDetailDrift.value.copy(this.handles.uDetailDrift.value);
      this.lakeHandles.uRainIntensity.value = this.handles.uRainIntensity.value;
    }
    // ── The interaction field follows the camera (whole-cell snaps keep the rings world-anchored).
    const field = fx.field;
    if (field) {
      field.setOrigin(this.camPos.x, this.camPos.z);
      const o = field.origin;
      this.fxStats.fieldOrigin = [o.x, o.z];
      // The materials sample in RENDER space: origin + the rebase shift.
      this.handles.uInteractionCenter.value.set(o.x + this.scene.position.x, o.z + this.scene.position.z);
      if (this.lakeHandles) this.lakeHandles.uInteractionCenter.value.copy(this.handles.uInteractionCenter.value);
      fx.centerRender.x = this.handles.uInteractionCenter.value.x;
      fx.centerRender.z = this.handles.uInteractionCenter.value.y;
      fx.rain = this.handles.uRainIntensity.value;
      if (fx.enabled) {
        // Rain: the drops big enough to ring outward, stamped only over water.
        if (rain > 0 && fx.provider) {
          const n = rainDropsThisTick(rain, fx.size, d, this.rainDebt);
          for (let i = 0; i < n; i++) {
            const x = o.x + (Math.random() - 0.5) * fx.size * 0.9;
            const z = o.z + (Math.random() - 0.5) * fx.size * 0.9;
            if (fx.provider.stillAt(x, z) === null) continue;
            field.disturbWorld(x, z, RAIN_DROP_STAMP.strength, RAIN_DROP_STAMP.radius);
            fx.stats.rain++;
          }
        }
        if (field.isActive) { field.update(d); fx.stats.steps++; }
      }
    }
    this.stepCraftWater(d);
    this.stepDrips(d);
    if (this.splashEmitter) {
      this.splashEmitter.update(d);
      // The CPU adapter (WebGL 2) billboards only when RENDERED — update() alone left
      // every splash at count 0 (never drawn) on the fallback backend.
      if (this.splashEmitter.backend === 'cpu') { try { (this.splashEmitter as CpuParticleAdapter).render(this.app!.renderer.cameras.main); } catch { /* no camera yet */ } }
    }
    if (this.dripEmitter) {
      this.dripEmitter.update(d);
      if (this.dripEmitter.backend === 'cpu') { try { (this.dripEmitter as CpuParticleAdapter).render(this.app!.renderer.cameras.main); } catch { /* no camera yet */ } }
    }
    this.fxStats.seaAmp = this.mod.ampScale; this.fxStats.seaRot = this.mod.dirRot; this.fxStats.foamJ = foamJ;
  }

  /** One pooled burst emitter for every splash (a body entering the water). */
  private buildSplashEmitter(): void {
    const app = this.app;
    if (!app || !waterFxTier(activeTier(), true, activePreset().particleCap ?? 0).particles) return; // W7: never on minimal
    if (this.splashEmitter) { this.scene.remove(this.splashEmitter.mesh); this.splashEmitter = null; }
    const cfg = {
      maxParticles: 600, lifetime: { min: 0.45, max: 0.9 }, startSpeed: { min: 2, max: 5.5 }, startSize: { min: 0.07, max: 0.22 },
      startColor: 0xe8f2f8, simulationSpace: 'world', shape: { type: 'cone', angle: 0.7, radius: 0.35, height: 0.15 },
      emission: { rateOverTime: 0 }, forces: { gravityModifier: 1, drag: 0.4 }, blending: THREE.NormalBlending, depthWrite: false,
      sizeOverLifetime: [{ time: 0, value: 0.6 }, { time: 0.3, value: 1 }, { time: 1, value: 0.4 }],
      alphaOverLifetime: [{ time: 0, value: 0.9 }, { time: 0.6, value: 0.7 }, { time: 1, value: 0 }],
    };
    try {
      const em = app.renderer.isWebGPU ? new GpuParticleEmitter(app.renderer as never, cfg as never) : new CpuParticleAdapter(cfg as never);
      em.mesh.frustumCulled = false; em.mesh.renderOrder = 5;
      const mat = em.mesh.material as THREE.Material & { premultipliedAlpha?: boolean };
      mat.premultipliedAlpha = true; // the weather's lesson: premultiplied rgb, blended premultiplied
      // The key is `softDistance` (the shader sweep, 2026-10-06): `{ distance }` behind an `as never` was silently ignored.
      try { applySoftParticles(mat as never, { softDistance: 0.6 }); } catch { /* the modifier is optional */ }
      this.scene.add(em.mesh);
      this.splashEmitter = em;
      const fx = waterFx();
      fx.splash = (x, y, z, count) => {
        // FRAME LAW: GPU kernels spawn in RENDER space, the CPU adapter in ABSOLUTE.
        const sp = em.backend === 'gpu' ? this.scene.position : this._splashZero;
        this._splashPos.set(x + sp.x, y + sp.y, z + sp.z);
        em.worldMatrix.setPosition(this._splashPos);
        em.emit(Math.max(1, Math.min(200, Math.round(count))));
        // R17: a crown ring spreads where the splash lands (absolute coords, on the live surface).
        this.crowns?.spawn(x, y, z, Math.min(1.6, 0.35 + count / 60));
      };
    } catch { this.splashEmitter = null; }
  }
  private readonly _splashZero = new THREE.Vector3(0, 0, 0);

  /**
   * W6 (2026-10-05): one small pooled emitter for the DRIPS off a body that just left deep water — drops a few
   * centimetres across falling under gravity, velocity-stretched into short streaks. Built only where the W7 ruling
   * allows particles; the FRAME LAW by backend like the splash.
   */
  private buildDripEmitter(): void {
    const app = this.app;
    if (!app || this.dripEmitter || !waterFxTier(activeTier(), true, activePreset().particleCap ?? 0).particles) return;
    const cfg = {
      maxParticles: 200, lifetime: { min: 0.35, max: 0.7 }, startSpeed: { min: 0, max: 0.35 }, startSize: { min: 0.018, max: 0.045 },
      startColor: 0xdcebf5, simulationSpace: 'world', shape: { type: 'sphere', radius: 0.2 },
      emission: { rateOverTime: 0 }, forces: { gravityModifier: 1, drag: 0.15 }, blending: THREE.NormalBlending, depthWrite: false,
      renderMode: 'stretched', velocityStretch: 1.6,
      sizeOverLifetime: [{ time: 0, value: 1 }, { time: 1, value: 0.85 }],
      alphaOverLifetime: [{ time: 0, value: 0.85 }, { time: 0.7, value: 0.6 }, { time: 1, value: 0 }],
    };
    try {
      const em = app.renderer.isWebGPU ? new GpuParticleEmitter(app.renderer as never, cfg as never) : new CpuParticleAdapter(cfg as never);
      em.mesh.frustumCulled = false; em.mesh.renderOrder = 5;
      const mat = em.mesh.material as THREE.Material & { premultipliedAlpha?: boolean };
      mat.premultipliedAlpha = true; // THE ONE PREMULTIPLY: straight rgb, blended premultiplied
      markMrtOverlay(mat); // a drop is an overlay: it must not rewrite the normal / velocity of the body behind it (THE OVERLAY LAW)
      this.scene.add(em.mesh);
      this.dripEmitter = em;
      waterFx().drip = (x, y, z, count) => {
        const sp = em.backend === 'gpu' ? this.scene.position : this._splashZero;
        this._splashPos.set(x + sp.x, y + sp.y, z + sp.z);
        em.worldMatrix.setPosition(this._splashPos);
        em.emit(Math.max(1, Math.min(24, Math.round(count))));
      };
    } catch { this.dripEmitter = null; }
  }

  /**
   * W6: the local player DRIPS after leaving deep water — PhysicsModule steps the wetness (`waterFx().wet`); here the
   * drops fall around the body at the tier's budget, tapering as the clock runs down. Nothing while riding or flying
   * (the clock still runs), nothing indoors.
   */
  private stepDrips(dt: number): void {
    const fx = waterFx(), w = fx.wet;
    if (!(w.dripLeft > 0) || !fx.particles || !fx.drip || atlas.hoverRide || atlas.skyFlight || atlas.spaceFlight || atlas.interiorId !== null) { this.dripDebt.debt = 0; return; }
    const n = sprayThisTick(dripRate(w, this.fxTier.dripsPerS), dt, this.dripDebt);
    if (n <= 0) return;
    // Alternate a hip and a shoulder height, a little off-centre each time: water runs off the whole body.
    const side = (this.dripSide = (this.dripSide + 1) % 4);
    const yaw = atlas.localYaw + side * 1.7;
    dripWater(atlas.localX + Math.cos(yaw) * 0.12, atlas.localY + (side & 1 ? 1.25 : 0.7), atlas.localZ + Math.sin(yaw) * 0.12, n);
  }

  /**
   * W3/W4 (2026-10-05): OTHER CRAFT over the water — read from the ONE choke point every craft module already feeds
   * (lib/audio/craft_feed.ts: versona journeys, the cargo lane, war hulls, other players' rides and ships). The nearest
   * CRAFT_WATER_MAX within CRAFT_WATER_RANGE_M of the camera: a hover craft ON the water wakes it (the Frostbite pair,
   * ≤ 20 Hz a craft, each stamp carrying the ticks it stands for) and throws spray unless it draws its own; a ship or a
   * freighter low over it raises downwash (spray on a ring under the hull; a ring stamp at most every
   * DOWNWASH_RING_EVERY_S a craft — a hovering ship cannot flood the field). Publishes `waterFx().craft` for the audio.
   * Nothing is allocated per frame but a new craft's motion record. ABSOLUTE coordinates.
   */
  private stepCraftWater(dt: number): void {
    const fx = waterFx(), out = fx.craft;
    out.count = 0;
    this.craftClockS += dt;
    const now = performance.now();
    const near = this.craftNear;
    this.craftN = 0;
    forEachCraft(now, this.collectCraft); // a bound field, never a fresh closure per frame
    const n = this.craftN;
    const ia = fx.interact;
    for (let i = 0; i < n; i++) {
      const r = near[i];
      const still = fx.provider?.stillAt(r.x, r.z) ?? null;
      if (still === null) continue;
      const live = fx.provider?.liveAt(r.x, r.z) ?? still;
      const kind = r.kind;
      const hullM = r.hullM > 0 ? r.hullM : CRAFT_HULL_M[kind];
      let m = this.craftMotion.get(r.id);
      if (!m) { m = { x: r.x, z: r.z, t: now, vx: 0, vz: 0, debt: { debt: 0 }, angle: 0 }; this.craftMotion.set(r.id, m); }
      const mdt = (now - m.t) / 1000;
      if (mdt > 0.02) {
        const k = Math.min(1, mdt * 3);
        m.vx += ((r.x - m.x) / mdt - m.vx) * k; m.vz += ((r.z - m.z) / mdt - m.vz) * k;
        if (mdt > 2) { m.vx = 0; m.vz = 0; }
        m.x = r.x; m.z = r.z; m.t = now;
      }
      const speed = Math.min(400, Math.hypot(m.vx, m.vz));
      // A hull's centre stands above its belly; a hover craft's report is its deck (the skirt ~1 m under it).
      const heightM = r.y - live - (kind === 'hover' ? 1.0 : 0.12 * hullM);
      const mode = craftWaterMode(kind, heightM, hullM);
      if (!mode) continue;
      ia.craftSeen++;
      let k = 0;
      if (mode === 'wake') {
        k = Math.min(1, 0.25 + speed / 30);
        if (this.craftWakeGate.due(r.id, this.craftClockS, 0.05)) {
          const scale = wakeRateScale(0.05);
          const pair = wakeStampPair(speed, Math.max(1.4, hullM * 0.3), hullM);
          const sp = Math.max(1e-3, speed), fxn = speed > 0.5 ? m.vx / sp : 0, fzn = speed > 0.5 ? m.vz / sp : 0;
          if (stampWater(r.x + fxn * pair.behind.along, r.z + fzn * pair.behind.along, pair.behind.strength * scale, pair.behind.radius)) ia.craftWakes++;
          if (speed > 1.5) stampWater(r.x + fxn * pair.ahead.along, r.z + fzn * pair.ahead.along, pair.ahead.strength * scale, pair.ahead.radius);
        }
        if (!r.ownSpray) {
          const ns = sprayThisTick(hoverSprayPerS(speed) * fx.particleScale, dt, m.debt);
          if (ns > 0) {
            const sp = Math.max(1e-3, speed);
            const bx = r.x - (m.vx / sp) * hullM * 0.5, bz = r.z - (m.vz / sp) * hullM * 0.5; // off the stern
            if (splashWater(bx, live, bz, ns, 3)) ia.craftSpray += ns;
          }
        }
      } else {
        const dw = downwashFor(heightM, hullM, speed, 0.6, this.craftDw);
        k = dw.k;
        if (k <= 0) continue;
        const ns = sprayThisTick(dw.sprayPerS * fx.particleScale, dt, m.debt);
        if (ns > 0) {
          m.angle += 2.39996;
          const sx = r.x + Math.cos(m.angle) * dw.sprayRadius, sz = r.z + Math.sin(m.angle) * dw.sprayRadius;
          if (splashWater(sx, fx.provider?.liveAt(sx, sz) ?? live, sz, ns, 3)) ia.craftSpray += ns;
        }
        if (this.craftRingGate.due(r.id, this.craftClockS, DOWNWASH_RING_EVERY_S) && stampWater(r.x, r.z, dw.ring.strength, dw.ring.radius)) ia.craftRings++;
      }
      // Publish for the audio (pooled entries — never a new object per frame once the list has grown).
      let e = out.list[out.count];
      if (!e) { e = { id: '', mode: 'wake', x: 0, y: 0, z: 0, k: 0, speed: 0 }; out.list[out.count] = e; }
      e.id = r.id; e.mode = mode; e.x = r.x; e.y = live; e.z = r.z; e.k = k; e.speed = speed;
      out.count++;
    }
    // Forget craft not reported for a while (the maps must not grow with every hull that ever flew).
    if (this.craftMotion.size > 48) for (const [id, m] of this.craftMotion) if (now - m.t > 10_000) this.craftMotion.delete(id);
    if (this.craftRingGate.size > 64) this.craftRingGate.prune(this.craftClockS, 20);
    if (this.craftWakeGate.size > 64) this.craftWakeGate.prune(this.craftClockS, 20);
  }

  /** Restore the atmosphere fog when surfacing / leaving the Commons. */
  private leaveWater(): void {
    this.submerged = false;
    atlas.submerged = false;
    this.wlState = 'above';
    this.hideUnderwaterView();
  }

  private disposeWater(): void {
    this.leaveWater();
    this.lastProbe = null;
    // A rebuild (seed change) used to create new foam / caustics / shafts over
    // the old ones — only dispose() released them (2026-09-12).
    if (this.foam) { this.foam.dispose(); this.foam = null; }
    if (this.foamField) { this.foamField.dispose(); this.foamField = null; }
    if (this.seaWaves) { const sw = this.seaWaves; this.seaWaves = null; disposeLater(sw); } // shader gaps R5 (its textures may be bound in the frame in flight)
    if (this.caustics) { this.caustics.dispose(); this.caustics = null; }
    if (this.shafts) { this.scene.remove(this.shafts.mesh); disposeLater(this.shafts.mesh.geometry); disposeLater(this.shafts.mesh.material as THREE.Material); this.shafts = null; stage4Stats.shaftsVisible = false; }
    if (this.medium) { for (const m of this.medium.meshes) { this.scene.remove(m); disposeLater(m.geometry); disposeLater(m.material as THREE.Material); } this.medium = null; }
    if (this.life) { this.life.dispose(); this.life = null; }
    if (this.crowns) { this.crowns.dispose(); this.crowns = null; }
    disposeLater(this.bedTex);
    this.bedTex = null;
    this.planarBody = null;
    // A stall-tripped worker stayed "broken" for the session: every later
    // build() was pinned to sliced main-thread bakes. A rebuild gets a fresh one.
    this.workerBroken = false;
    if (this.mesh) {
      this.scene.remove(this.mesh);
      disposeLater(this.mesh.geometry);
      disposeLater(this.mesh.material as THREE.Material);
      this.mesh = null;
    }
    if (this.lakeMesh) {
      this.scene.remove(this.lakeMesh);
      disposeLater(this.lakeMesh.geometry);
      disposeLater(this.lakeMesh.material as THREE.Material);
      this.lakeMesh = null;
    }
    // Deferred like the meshes above (warp-teardown law): the lake depth
    // texture, the sea normal map, the overlay's mesh and the shoreline field
    // texture were all bound in the frame in flight when a seed / world swap
    // tore the water down — synchronous dispose() is the "used in submit while
    // destroyed" class.
    disposeLater(this.lakeDepthTex);
    this.lakeDepthTex = null;
    this.lakeHandles = null;
    this.lakeRegion = null;
    this.lakeBake = null;
    if (this.overlay) {
      this.scene.remove(this.overlay.mesh);
      disposeLater(this.overlay);
      this.overlay = null;
    }
    this.bakeWorker?.terminate();
    this.bakeWorker = null;
    this.pendingShoreId = 0;
    this.pendingShoreAt = null;
    this.pendingLakeId = 0;
    disposeLater(this.shoreline);
    this.shoreline = null;
    disposeLater(this.normalMap);
    this.normalMap = null;
    this.handles = null;
    if (this.splashEmitter) { this.scene.remove(this.splashEmitter.mesh); disposeLater(this.splashEmitter.mesh.geometry); disposeLater(this.splashEmitter.mesh.material as THREE.Material); this.splashEmitter = null; }
    if (this.dripEmitter) { this.scene.remove(this.dripEmitter.mesh); disposeLater(this.dripEmitter.mesh.geometry); disposeLater(this.dripEmitter.mesh.material as THREE.Material); this.dripEmitter = null; }
    const fx = waterFx();
    if (fx.provider) fx.provider = null;
    fx.splash = null;
    fx.drip = null;
    fx.craft.count = 0;
    this.emitterTry = ''; // a rebuilt water tries its lazy emitters again
  }

  dispose(): void {
    this.disposeWater(); // foam, caustics and the shafts go with the water
    // Shader gaps R1: the descriptor forgets this module's sources; the ground's lake / river branch stands down.
    setWaterBodySources(null);
    setWaterBodyLabels(null);
    setBedBodies(null);
  }
}
